import React, { useRef, useEffect, useState, useCallback } from 'react'
import './DataIASection.css'

// ─── Reveal ───────────────────────────────────────────────────────────────────
function useReveal(threshold = 0.1) {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect() }
    }, { threshold })
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible]
}

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  green:  [0,   255, 136],
  blue:   [0,   170, 255],
  purple: [160, 100, 255],
  orange: [255, 150,  40],
  gold:   [255, 210,  55],
  red:    [255,  75,  75],
  teal:   [0,   210, 200],
}
const rgba = ([r, g, b], a = 1) => `rgba(${r},${g},${b},${a})`

// ─── Crisp HiDPI canvas setup ─────────────────────────────────────────────────
function setupCanvas(canvas, cssW, cssH) {
  const dpr = Math.min(window.devicePixelRatio || 1, 3)
  canvas.width  = Math.round(cssW * dpr)
  canvas.height = Math.round(cssH * dpr)
  canvas.style.width  = cssW + 'px'
  canvas.style.height = cssH + 'px'
  const ctx = canvas.getContext('2d')
  ctx.scale(dpr, dpr)
  return { ctx, W: cssW, H: cssH, dpr }
}

// ─── Rounded rect ─────────────────────────────────────────────────────────────
function rr(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y,     x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x,     y + h, r)
  ctx.arcTo(x,     y + h, x,     y,     r)
  ctx.arcTo(x,     y,     x + w, y,     r)
  ctx.closePath()
}

// ─────────────────────────────────────────────────────────────────────────────
//  MCD FOOTBALL CANVAS
// ─────────────────────────────────────────────────────────────────────────────
function MCDCanvas({ active }) {
  const canvasRef  = useRef(null)
  const animRef    = useRef(null)
  const entRef     = useRef([])
  const mouseRef   = useRef({ down: false, drag: null, ox: 0, oy: 0 })
  const panRef     = useRef({ x: 20, y: 20, startX: 0, startY: 0 })
  const scaleRef   = useRef(0.86)
  const hovRef     = useRef(null)
  const dimRef     = useRef({ W: 900, H: 540 })

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return

    const init = () => {
      const parent = canvas.parentElement
      if (!parent) return
      const cssW = parent.clientWidth  || 900
      const cssH = parent.clientHeight || 540
      dimRef.current = { W: cssW, H: cssH }

      const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)

      const ROW  = 21
      const PAD  = { x: 14, top: 8, hdr: 30 }

      const SCHEMA = [
        { id:'pays',        label:'PAYS',          col:C.teal,
          f:[{n:'id_pays',pk:true},{n:'nom_pays'},{n:'confederation'}],
          rx:0.06, ry:0.08 },
        { id:'ligue',       label:'LIGUE',         col:C.green,
          f:[{n:'id_ligue',pk:true},{n:'nom_ligue'},{n:'niveau'},{n:'id_pays',fk:true}],
          rx:0.30, ry:0.04 },
        { id:'competition', label:'COMPÉTITION',   col:C.gold,
          f:[{n:'id_competition',pk:true},{n:'nom'},{n:'type'},{n:'confederation'}],
          rx:0.54, ry:0.04 },
        { id:'saison',      label:'SAISON',        col:C.purple,
          f:[{n:'id_saison',pk:true},{n:'annee_debut'},{n:'annee_fin'}],
          rx:0.79, ry:0.08 },
        { id:'club',        label:'CLUB',          col:C.green,
          f:[{n:'id_club',pk:true},{n:'nom_club'},{n:'ville'},{n:'stade'},{n:'id_ligue',fk:true},{n:'id_pays',fk:true}],
          rx:0.16, ry:0.44 },
        { id:'joueur',      label:'JOUEUR',        col:C.blue,
          f:[{n:'id_joueur',pk:true},{n:'nom'},{n:'prenom'},{n:'date_naissance'},{n:'poste'},{n:'nationalite'},{n:'id_club',fk:true}],
          rx:0.02, ry:0.67 },
        { id:'match',       label:'MATCH',         col:C.orange,
          f:[{n:'id_match',pk:true},{n:'date_match'},{n:'id_club_dom',fk:true},{n:'id_club_ext',fk:true},{n:'score_dom'},{n:'score_ext'},{n:'id_competition',fk:true},{n:'id_saison',fk:true}],
          rx:0.46, ry:0.45 },
        { id:'classement',  label:'CLASSEMENT',    col:C.purple,
          f:[{n:'id_classement',pk:true},{n:'id_club',fk:true},{n:'id_competition',fk:true},{n:'id_saison',fk:true},{n:'pts'},{n:'j'},{n:'g / n / p'},{n:'bp / bc'}],
          rx:0.75, ry:0.43 },
        { id:'stat_joueur', label:'STATS JOUEUR',  col:C.red,
          f:[{n:'id_stat',pk:true},{n:'id_joueur',fk:true},{n:'id_match',fk:true},{n:'buts'},{n:'passes_d'},{n:'minutes'},{n:'cartons_j / r'}],
          rx:0.30, ry:0.72 },
        { id:'transfert',   label:'TRANSFERT',     col:C.gold,
          f:[{n:'id_transfert',pk:true},{n:'id_joueur',fk:true},{n:'id_club_dep',fk:true},{n:'id_club_arr',fk:true},{n:'montant_M€'},{n:'date_transfert'}],
          rx:0.60, ry:0.74 },
      ]

      ctx.font = 'bold 11px "DM Mono", monospace'
      const entities = SCHEMA.map(s => {
        const labelW = ctx.measureText(s.label).width
        ctx.font = '10px "DM Mono", monospace'
        const maxField = Math.max(...s.f.map(f => ctx.measureText(f.n).width))
        const w = Math.max(labelW, maxField) + PAD.x * 2 + 30
        const h = PAD.hdr + s.f.length * ROW + PAD.top + 4
        return { ...s, x: s.rx * W, y: s.ry * H, w: Math.max(w, 152), h }
      })
      entRef.current = entities

      const RELS = [
        { a:'pays',        b:'ligue',       card:'1,N' },
        { a:'ligue',       b:'club',        card:'1,N' },
        { a:'pays',        b:'club',        card:'1,N' },
        { a:'club',        b:'joueur',      card:'1,N' },
        { a:'competition', b:'match',       card:'1,N' },
        { a:'saison',      b:'match',       card:'1,N' },
        { a:'club',        b:'match',       card:'2,N', dash:true },
        { a:'match',       b:'stat_joueur', card:'1,N' },
        { a:'joueur',      b:'stat_joueur', card:'1,N' },
        { a:'club',        b:'classement',  card:'1,N' },
        { a:'competition', b:'classement',  card:'1,N' },
        { a:'saison',      b:'classement',  card:'1,N' },
        { a:'joueur',      b:'transfert',   card:'1,N' },
        { a:'club',        b:'transfert',   card:'1,N', dash:true },
        { a:'ligue',       b:'competition', card:'0,N' },
      ]

      const draw = () => {
        ctx.clearRect(0, 0, W, H)
        ctx.save()
        ctx.translate(panRef.current.x, panRef.current.y)
        ctx.scale(scaleRef.current, scaleRef.current)

        const ents  = entRef.current
        const byId  = Object.fromEntries(ents.map(e => [e.id, e]))
        const hov   = hovRef.current

        RELS.forEach(rel => {
          const a = byId[rel.a], b = byId[rel.b]
          if (!a || !b) return
          const isHov = hov === a.id || hov === b.id
          const ax = a.x + a.w / 2, ay = a.y + a.h / 2
          const bx = b.x + b.w / 2, by = b.y + b.h / 2
          const mx = (ax + bx) / 2,  my = (ay + by) / 2
          const dx = bx - ax, dy = by - ay
          const len = Math.hypot(dx, dy) || 1
          const cpx = mx - (dy / len) * 45, cpy = my + (dx / len) * 45

          const ecol = rel.dash ? C.orange : C.green

          if (isHov) {
            ctx.save()
            ctx.beginPath()
            ctx.moveTo(ax, ay)
            ctx.quadraticCurveTo(cpx, cpy, bx, by)
            ctx.strokeStyle = rgba(ecol, 0.35)
            ctx.lineWidth   = 8
            ctx.filter      = 'blur(4px)'
            ctx.stroke()
            ctx.filter = 'none'
            ctx.restore()
          }

          ctx.beginPath()
          ctx.moveTo(ax, ay)
          ctx.quadraticCurveTo(cpx, cpy, bx, by)
          ctx.strokeStyle = rgba(ecol, isHov ? 0.9 : 0.22)
          ctx.lineWidth   = isHov ? 1.8 : 0.9
          ctx.setLineDash(rel.dash ? [5, 4] : [])
          ctx.stroke()
          ctx.setLineDash([])

          const ang = Math.atan2(by - cpy, bx - cpx)
          const al  = 8
          ctx.beginPath()
          ctx.moveTo(bx - al * Math.cos(ang - 0.38), by - al * Math.sin(ang - 0.38))
          ctx.lineTo(bx, by)
          ctx.lineTo(bx - al * Math.cos(ang + 0.38), by - al * Math.sin(ang + 0.38))
          ctx.strokeStyle = rgba(ecol, isHov ? 0.95 : 0.30)
          ctx.lineWidth   = isHov ? 1.6 : 0.8
          ctx.stroke()

          ctx.font      = isHov ? 'bold 10px "DM Mono",monospace' : '9px "DM Mono",monospace'
          ctx.textAlign = 'center'
          const tw = ctx.measureText(rel.card).width + 8
          const tx = cpx, ty = cpy - 12
          rr(ctx, tx - tw / 2, ty - 9, tw, 13, 4)
          ctx.fillStyle = rgba(ecol, isHov ? 0.2 : 0.08)
          ctx.fill()
          ctx.fillStyle = rgba(ecol, isHov ? 1 : 0.55)
          ctx.fillText(rel.card, tx, ty)
        })

        ents.forEach(e => {
          const isHov = hov === e.id
          const { x, y, w, h, col } = e

          ctx.save()
          ctx.shadowColor = rgba(col, isHov ? 0.65 : 0.18)
          ctx.shadowBlur  = isHov ? 32 : 14
          rr(ctx, x, y, w, h, 10)
          ctx.fillStyle = 'transparent'
          ctx.fill()
          ctx.restore()

          rr(ctx, x, y, w, h, 10)
          ctx.fillStyle = 'rgba(7, 12, 10, 0.97)'
          ctx.fill()

          rr(ctx, x, y, w, h, 10)
          const tint = ctx.createLinearGradient(x, y, x + w * 0.7, y + h * 0.7)
          tint.addColorStop(0, rgba(col, isHov ? 0.09 : 0.05))
          tint.addColorStop(1, rgba(col, 0))
          ctx.fillStyle = tint
          ctx.fill()

          rr(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 9.5)
          const bd = ctx.createLinearGradient(x, y, x + w, y + h)
          bd.addColorStop(0,   rgba(col, isHov ? 1.0 : 0.55))
          bd.addColorStop(0.4, rgba(col, isHov ? 0.6 : 0.22))
          bd.addColorStop(1,   rgba(col, isHov ? 0.8 : 0.40))
          ctx.strokeStyle = bd
          ctx.lineWidth   = isHov ? 1.6 : 1.0
          ctx.stroke()

          ctx.save()
          ctx.beginPath()
          ctx.moveTo(x + 10, y)
          ctx.arcTo(x + w, y,     x + w, y + 10, 10)
          ctx.lineTo(x + w, y + PAD.hdr)
          ctx.lineTo(x,     y + PAD.hdr)
          ctx.lineTo(x,     y + 10)
          ctx.arcTo(x, y,   x + 10, y, 10)
          ctx.closePath()
          const hg = ctx.createLinearGradient(x, y, x, y + PAD.hdr)
          hg.addColorStop(0, rgba(col, isHov ? 0.32 : 0.20))
          hg.addColorStop(1, rgba(col, isHov ? 0.10 : 0.06))
          ctx.fillStyle = hg; ctx.fill()
          ctx.restore()

          ctx.beginPath()
          ctx.moveTo(x + 1, y + PAD.hdr)
          ctx.lineTo(x + w - 1, y + PAD.hdr)
          const sg = ctx.createLinearGradient(x, 0, x + w, 0)
          sg.addColorStop(0,   rgba(col, 0))
          sg.addColorStop(0.2, rgba(col, isHov ? 0.9 : 0.45))
          sg.addColorStop(0.8, rgba(col, isHov ? 0.9 : 0.45))
          sg.addColorStop(1,   rgba(col, 0))
          ctx.strokeStyle = sg; ctx.lineWidth = 0.8; ctx.stroke()

          ctx.textAlign = 'center'
          ctx.font      = 'bold 11px "DM Mono", monospace'
          ctx.fillStyle = rgba(col, 0.25)
          ctx.fillText(e.label, x + w / 2 + 0.8, y + PAD.hdr * 0.7 + 0.8)
          ctx.fillStyle = rgba(col, isHov ? 1 : 0.95)
          ctx.fillText(e.label, x + w / 2, y + PAD.hdr * 0.7)

          ctx.beginPath()
          ctx.arc(x + w - 9, y + 10, 3.5, 0, Math.PI * 2)
          ctx.fillStyle = rgba(col, isHov ? 1 : 0.55)
          if (isHov) { ctx.shadowColor = rgba(col, 1); ctx.shadowBlur = 10 }
          ctx.fill()
          ctx.shadowBlur = 0

          e.f.forEach((f, fi) => {
            const fy     = y + PAD.hdr + PAD.top + fi * ROW
            const isLast = fi === e.f.length - 1

            if (fi % 2 === 0) {
              ctx.fillStyle = 'rgba(255,255,255,0.016)'
              ctx.fillRect(x + 1, fy - 1, w - 2, ROW)
            }

            if (f.pk || f.fk) {
              const bc  = f.pk ? C.gold : C.orange
              const btx = f.pk ? 'PK'   : 'FK'
              const bx2 = x + 5, by2 = fy + 3, bw = 19, bh = 14
              rr(ctx, bx2, by2, bw, bh, 3)
              ctx.fillStyle   = rgba(bc, 0.14); ctx.fill()
              rr(ctx, bx2, by2, bw, bh, 3)
              ctx.strokeStyle = rgba(bc, 0.55); ctx.lineWidth = 0.7; ctx.stroke()
              ctx.fillStyle   = rgba(bc, 0.95)
              ctx.font        = 'bold 8px "DM Mono", monospace'
              ctx.textAlign   = 'center'
              ctx.fillText(btx, bx2 + bw / 2, by2 + bh * 0.75)
            }

            ctx.font      = f.pk ? 'bold 10.5px "DM Mono",monospace' : '10px "DM Mono",monospace'
            ctx.textAlign = 'left'
            ctx.fillStyle = f.pk
              ? rgba(C.gold,   0.95)
              : f.fk
                ? rgba(C.orange, 0.82)
                : 'rgba(215,232,224,0.75)'
            ctx.fillText(f.n, x + 30, fy + ROW * 0.72)

            if (!isLast) {
              ctx.beginPath()
              ctx.moveTo(x + 5,     fy + ROW - 0.5)
              ctx.lineTo(x + w - 5, fy + ROW - 0.5)
              ctx.strokeStyle = 'rgba(255,255,255,0.05)'
              ctx.lineWidth   = 0.5; ctx.stroke()
            }
          })
        })

        ctx.restore()
        animRef.current = requestAnimationFrame(draw)
      }

      cancelAnimationFrame(animRef.current)
      draw()
    }

    const getRect  = () => canvasRef.current?.getBoundingClientRect() || { left:0, top:0 }
    const toWorld  = (cx, cy) => ({
      wx: (cx - panRef.current.x) / scaleRef.current,
      wy: (cy - panRef.current.y) / scaleRef.current,
    })
    const hitEnt   = (wx, wy) =>
      entRef.current.find(e => wx >= e.x && wx <= e.x + e.w && wy >= e.y && wy <= e.y + e.h) || null

    const onDown = ev => {
      const { left, top } = getRect()
      const cx = ev.clientX - left, cy = ev.clientY - top
      const { wx, wy } = toWorld(cx, cy)
      const h = hitEnt(wx, wy)
      mouseRef.current = { down:true, drag:h, ox: h ? wx - h.x : 0, oy: h ? wy - h.y : 0 }
      if (!h) { panRef.current.startX = cx - panRef.current.x; panRef.current.startY = cy - panRef.current.y }
    }
    const onMove = ev => {
      const { left, top } = getRect()
      const cx = ev.clientX - left, cy = ev.clientY - top
      const { wx, wy } = toWorld(cx, cy)
      hovRef.current = hitEnt(wx, wy)?.id || null
      canvas.style.cursor = hovRef.current ? 'grab' : mouseRef.current.down ? 'grabbing' : 'default'
      if (!mouseRef.current.down) return
      if (mouseRef.current.drag) {
        mouseRef.current.drag.x = wx - mouseRef.current.ox
        mouseRef.current.drag.y = wy - mouseRef.current.oy
      } else {
        panRef.current.x = cx - panRef.current.startX
        panRef.current.y = cy - panRef.current.startY
      }
    }
    const onUp    = () => { mouseRef.current.down = false; mouseRef.current.drag = null }
    const onWheel = ev => {
      ev.preventDefault()
      scaleRef.current = Math.max(0.3, Math.min(2.5, scaleRef.current * (ev.deltaY > 0 ? 0.92 : 1.09)))
    }
    const onResize = () => { cancelAnimationFrame(animRef.current); requestAnimationFrame(init) }

    canvas.addEventListener('mousedown',  onDown)
    canvas.addEventListener('mousemove',  onMove)
    canvas.addEventListener('mouseup',    onUp)
    canvas.addEventListener('mouseleave', onUp)
    canvas.addEventListener('wheel',      onWheel, { passive:false })
    window.addEventListener('resize',     onResize)

    const raf = requestAnimationFrame(init)
    return () => {
      cancelAnimationFrame(animRef.current)
      cancelAnimationFrame(raf)
      canvas.removeEventListener('mousedown',  onDown)
      canvas.removeEventListener('mousemove',  onMove)
      canvas.removeEventListener('mouseup',    onUp)
      canvas.removeEventListener('mouseleave', onUp)
      canvas.removeEventListener('wheel',      onWheel)
      window.removeEventListener('resize',     onResize)
    }
  }, [active])

  return <canvas ref={canvasRef} className="viz-canvas-el" style={{ display:'block' }} />
}

// ─── DATA STREAM ──────────────────────────────────────────────────────────────
function DataStreamCanvas({ active }) {
  const canvasRef = useRef(null)
  const animRef   = useRef(null)

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return

    const init = () => {
      const cssW = canvas.parentElement?.clientWidth  || 800
      const cssH = canvas.parentElement?.clientHeight || 400
      const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)

      const streams = [
        { col:C.green,  label:'Conversion %', data:[] },
        { col:C.blue,   label:'Bounce Rate',  data:[] },
        { col:C.purple, label:'Session Time', data:[] },
        { col:C.orange, label:'Engagement',   data:[] },
      ]
      streams.forEach(s => { for (let i=0;i<W;i++) s.data.push(0.3+Math.random()*0.4) })

      const tick = () => {
        ctx.clearRect(0,0,W,H)
        for (let i=0;i<=6;i++) {
          ctx.beginPath(); ctx.moveTo((W/6)*i,0); ctx.lineTo((W/6)*i,H)
          ctx.strokeStyle='rgba(255,255,255,0.03)'; ctx.lineWidth=1; ctx.stroke()
        }
        streams.forEach((s,si) => {
          const last=s.data[s.data.length-1]
          s.data.push(Math.max(0.08,Math.min(0.92,last+(Math.random()-0.5)*0.06)))
          if(s.data.length>W) s.data.shift()
          const baseY=H*0.14+si*H*0.195, amp=H*0.075

          ctx.beginPath()
          s.data.forEach((v,xi)=>{ xi===0?ctx.moveTo(xi,baseY-v*amp):ctx.lineTo(xi,baseY-v*amp) })
          ctx.lineTo(W,baseY); ctx.lineTo(0,baseY); ctx.closePath()
          const g=ctx.createLinearGradient(0,baseY-amp,0,baseY)
          g.addColorStop(0,rgba(s.col,0.2)); g.addColorStop(1,rgba(s.col,0))
          ctx.fillStyle=g; ctx.fill()

          ctx.beginPath()
          s.data.forEach((v,xi)=>{ xi===0?ctx.moveTo(xi,baseY-v*amp):ctx.lineTo(xi,baseY-v*amp) })
          ctx.strokeStyle=rgba(s.col,0.9); ctx.lineWidth=1.5; ctx.stroke()

          const curY=baseY-s.data[s.data.length-1]*amp
          const pulse=(Date.now()%1400)/1400
          ctx.beginPath(); ctx.arc(W-3,curY,4+pulse*11,0,Math.PI*2)
          ctx.strokeStyle=rgba(s.col,0.4*(1-pulse)); ctx.lineWidth=1; ctx.stroke()
          ctx.beginPath(); ctx.arc(W-3,curY,4.5,0,Math.PI*2)
          ctx.fillStyle=rgba(s.col,1); ctx.fill()

          ctx.fillStyle=rgba(s.col,0.75); ctx.font='11px "DM Mono",monospace'; ctx.textAlign='left'
          ctx.fillText(s.label,10,baseY-amp-6)
          ctx.fillStyle=rgba(s.col,0.95); ctx.font='bold 12px "DM Mono",monospace'; ctx.textAlign='right'
          ctx.fillText(`${Math.round(s.data[s.data.length-1]*100)}%`,W-16,curY-8)
        })
        animRef.current=requestAnimationFrame(tick)
      }
      tick()
    }

    const raf=requestAnimationFrame(init)
    const onResize=()=>{ cancelAnimationFrame(animRef.current); requestAnimationFrame(init) }
    window.addEventListener('resize',onResize)
    return ()=>{ cancelAnimationFrame(animRef.current); cancelAnimationFrame(raf); window.removeEventListener('resize',onResize) }
  }, [active])

  return <canvas ref={canvasRef} className="viz-canvas-el" style={{ display:'block' }} />
}

const AXES_RADAR = [
  { label:'Machine Learning', v:0.75 },
  { label:'Data Viz',         v:0.82 },
  { label:'SQL / BD',         v:0.88 },
  { label:'Python',           v:0.85 },
  { label:'Statistiques',     v:0.70 },
  { label:'IA Web',           v:0.78 },
]

// ─── RADAR ────────────────────────────────────────────────────────────────────
function RadarCanvas({ active }) {
  const canvasRef = useRef(null)
  const animRef   = useRef(null)

  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current
    if (!canvas) return

    const init = () => {
      const cssW = canvas.parentElement?.clientWidth  || 800
      const cssH = canvas.parentElement?.clientHeight || 400
      const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)
      const cx=W/2, cy=H/2-10, R=Math.min(W,H)*0.34, N=AXES_RADAR.length
      const pt=(i,r)=>({ x:cx+r*Math.cos((i/N)*Math.PI*2-Math.PI/2), y:cy+r*Math.sin((i/N)*Math.PI*2-Math.PI/2) })
      let progress=0

      const tick=()=>{
        progress=Math.min(1,progress+0.02)
        ctx.clearRect(0,0,W,H)
        const ease=1-Math.pow(1-progress,3)

        for(let ring=1;ring<=5;ring++){
          ctx.beginPath()
          for(let i=0;i<=N;i++){const p=pt(i%N,(ring/5)*R);i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)}
          ctx.strokeStyle=`rgba(255,255,255,${ring===5?0.1:0.04})`; ctx.lineWidth=1; ctx.stroke()
        }
        AXES_RADAR.forEach((_,i)=>{
          const p=pt(i,R)
          ctx.beginPath(); ctx.moveTo(cx,cy); ctx.lineTo(p.x,p.y)
          ctx.strokeStyle='rgba(255,255,255,0.07)'; ctx.lineWidth=1; ctx.stroke()
        })
        ctx.beginPath()
        AXES_RADAR.forEach((ax,i)=>{const p=pt(i,R*ax.v*ease);i===0?ctx.moveTo(p.x,p.y):ctx.lineTo(p.x,p.y)})
        ctx.closePath()
        const fill=ctx.createRadialGradient(cx,cy,0,cx,cy,R)
        fill.addColorStop(0,'rgba(0,255,136,0.28)'); fill.addColorStop(1,'rgba(0,170,255,0.06)')
        ctx.fillStyle=fill; ctx.fill()
        ctx.strokeStyle='rgba(0,255,136,0.8)'; ctx.lineWidth=2; ctx.stroke()

        AXES_RADAR.forEach((ax,i)=>{
          const p=pt(i,R*ax.v*ease)
          const g=ctx.createRadialGradient(p.x,p.y,0,p.x,p.y,14)
          g.addColorStop(0,'rgba(0,255,136,0.5)'); g.addColorStop(1,'rgba(0,255,136,0)')
          ctx.beginPath(); ctx.arc(p.x,p.y,14,0,Math.PI*2); ctx.fillStyle=g; ctx.fill()
          ctx.beginPath(); ctx.arc(p.x,p.y,5,0,Math.PI*2); ctx.fillStyle='#00ff88'; ctx.fill()

          const lp=pt(i,R*1.24), vp=pt(i,R*ax.v*ease*0.58)
          ctx.fillStyle='rgba(255,255,255,0.65)'; ctx.font='11px "DM Mono",monospace'
          ctx.textAlign=lp.x<cx-10?'right':lp.x>cx+10?'left':'center'
          ctx.fillText(ax.label,lp.x,lp.y+4)
          ctx.fillStyle='rgba(0,255,136,0.95)'; ctx.font='bold 11px "DM Mono",monospace'; ctx.textAlign='center'
          ctx.fillText(`${Math.round(ax.v*ease*100)}%`,vp.x,vp.y+4)
        })
        if(progress<1) animRef.current=requestAnimationFrame(tick)
      }
      tick()
    }

    const raf=requestAnimationFrame(init)
    const onResize=()=>{ cancelAnimationFrame(animRef.current); requestAnimationFrame(init) }
    window.addEventListener('resize',onResize)
    return ()=>{ cancelAnimationFrame(animRef.current); cancelAnimationFrame(raf); window.removeEventListener('resize',onResize) }
  }, [active])

  return <canvas ref={canvasRef} className="viz-canvas-el" style={{ display:'block' }} />
}

// ─────────────────────────────────────────────────────────────────────────────
//  SQL LIVE DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────

// ── Fake football database ────────────────────────────────────────────────────
const DB = {
  clubs: [
    { id_club:1,  nom_club:'PSG',        ville:'Paris',       pts:82, j:34, g:26, n:4,  p:4,  bp:91, bc:28, budget_M:800 },
    { id_club:2,  nom_club:'Marseille',  ville:'Marseille',   pts:71, j:34, g:22, n:5,  p:7,  bp:74, bc:41, budget_M:180 },
    { id_club:3,  nom_club:'Lyon',       ville:'Lyon',        pts:65, j:34, g:19, n:8,  p:7,  bp:68, bc:44, budget_M:210 },
    { id_club:4,  nom_club:'Monaco',     ville:'Monaco',      pts:68, j:34, g:21, n:5,  p:8,  bp:72, bc:39, budget_M:190 },
    { id_club:5,  nom_club:'Lille',      ville:'Lille',       pts:62, j:34, g:18, n:8,  p:8,  bp:59, bc:38, budget_M:120 },
    { id_club:6,  nom_club:'Nice',       ville:'Nice',        pts:58, j:34, g:17, n:7,  p:10, bp:54, bc:42, budget_M:100 },
    { id_club:7,  nom_club:'Lens',       ville:'Lens',        pts:55, j:34, g:16, n:7,  p:11, bp:51, bc:45, budget_M: 90 },
    { id_club:8,  nom_club:'Rennes',     ville:'Rennes',      pts:52, j:34, g:15, n:7,  p:12, bp:48, bc:47, budget_M: 95 },
    { id_club:9,  nom_club:'Montpellier',ville:'Montpellier', pts:44, j:34, g:12, n:8,  p:14, bp:42, bc:52, budget_M: 60 },
    { id_club:10, nom_club:'Strasbourg', ville:'Strasbourg',  pts:40, j:34, g:11, n:7,  p:16, bp:38, bc:58, budget_M: 55 },
  ],
  joueurs: [
    { id_joueur:1,  nom:'Mbappé',     prenom:'Kylian',   poste:'ATT', id_club:1, buts:28, passes_d:12, minutes:2890, valeur_M:180 },
    { id_joueur:2,  nom:'Ben Yedder', prenom:'Wissam',   poste:'ATT', id_club:4, buts:22, passes_d: 8, minutes:2710, valeur_M: 28 },
    { id_joueur:3,  nom:'David',      prenom:'Jonathan', poste:'ATT', id_club:5, buts:20, passes_d: 6, minutes:2840, valeur_M: 35 },
    { id_joueur:4,  nom:'Neymar',     prenom:'Jr',       poste:'MIL', id_club:1, buts:15, passes_d:16, minutes:2100, valeur_M:120 },
    { id_joueur:5,  nom:'Payet',      prenom:'Dimitri',  poste:'MIL', id_club:2, buts: 9, passes_d:14, minutes:2780, valeur_M: 12 },
    { id_joueur:6,  nom:'Camavinga',  prenom:'Eduardo',  poste:'MIL', id_club:3, buts: 4, passes_d:11, minutes:2650, valeur_M: 55 },
    { id_joueur:7,  nom:'Marquinhos', prenom:'',         poste:'DEF', id_club:1, buts: 5, passes_d: 3, minutes:2980, valeur_M: 60 },
    { id_joueur:8,  nom:'Saliba',     prenom:'William',  poste:'DEF', id_club:2, buts: 3, passes_d: 2, minutes:3020, valeur_M: 40 },
    { id_joueur:9,  nom:'Botman',     prenom:'Sven',     poste:'DEF', id_club:5, buts: 2, passes_d: 1, minutes:2900, valeur_M: 30 },
    { id_joueur:10, nom:'Lloris',     prenom:'Hugo',     poste:'GK',  id_club:3, buts: 0, passes_d: 0, minutes:2960, valeur_M: 18 },
  ],
  matchs: [
    { id_match:1, id_club_dom:1, id_club_ext:2, score_dom:3, score_ext:1, saison:'2024-25' },
    { id_match:2, id_club_dom:4, id_club_ext:3, score_dom:2, score_ext:2, saison:'2024-25' },
    { id_match:3, id_club_dom:5, id_club_ext:6, score_dom:1, score_ext:0, saison:'2024-25' },
    { id_match:4, id_club_dom:1, id_club_ext:4, score_dom:4, score_ext:1, saison:'2024-25' },
    { id_match:5, id_club_dom:2, id_club_ext:5, score_dom:2, score_ext:2, saison:'2024-25' },
    { id_match:6, id_club_dom:3, id_club_ext:1, score_dom:0, score_ext:2, saison:'2024-25' },
    { id_match:7, id_club_dom:6, id_club_ext:7, score_dom:1, score_ext:1, saison:'2024-25' },
    { id_match:8, id_club_dom:8, id_club_ext:9, score_dom:3, score_ext:0, saison:'2024-25' },
  ],
  transferts: [
    { id_transfert:1, id_joueur:1, id_club_dep:10, id_club_arr:1, montant_M:180, annee:2022 },
    { id_transfert:2, id_joueur:4, id_club_dep:9,  id_club_arr:1, montant_M:222, annee:2021 },
    { id_transfert:3, id_joueur:6, id_club_dep:8,  id_club_arr:3, montant_M: 40, annee:2023 },
    { id_transfert:4, id_joueur:8, id_club_dep:7,  id_club_arr:2, montant_M: 35, annee:2022 },
    { id_transfert:5, id_joueur:9, id_club_dep:6,  id_club_arr:5, montant_M: 30, annee:2023 },
  ],
}

// ── SQL mini-interpreter ──────────────────────────────────────────────────────
function runSQL(query) {
  const q = query.trim().toUpperCase()
  const clubName   = id => DB.clubs.find(c => c.id_club === id)?.nom_club || '?'
  const joueurName = id => { const j = DB.joueurs.find(x => x.id_joueur === id); return j ? `${j.prenom} ${j.nom}`.trim() : '?' }

  if (q.includes('FROM CLUBS') && !q.includes('JOIN')) {
    let rows = [...DB.clubs]
    if (q.includes('ORDER BY PTS'))    rows.sort((a,b) => b.pts - a.pts)
    if (q.includes('ORDER BY BUDGET')) rows.sort((a,b) => b.budget_M - a.budget_M)
    if (q.includes('LIMIT')) { const n = parseInt(q.match(/LIMIT (\d+)/)?.[1] || 10); rows = rows.slice(0, n) }
    const cols = q.includes('NOM_CLUB, BUDGET') ? ['nom_club','budget_M'] : ['nom_club','pts','j','g','n','p','bp','bc']
    return {
      columns: cols,
      rows: rows.map(r => cols.map(c => r[c])),
      vizType: 'bar',
      vizData: rows.map(r => ({ label: r.nom_club, value: r.pts, color: C.green })),
      vizLabel: 'Points',
    }
  }

  if (q.includes('FROM JOUEURS') && q.includes('JOIN CLUBS')) {
    const joined = DB.joueurs.map(j => ({ ...j, club: DB.clubs.find(c => c.id_club === j.id_club) }))
    const cols = ['nom','poste','buts','valeur_M','nom_club']
    const rows = joined.sort((a,b) => b.buts - a.buts)
    return {
      columns: cols,
      rows: rows.map(r => cols.map(c => c === 'nom_club' ? r.club?.nom_club : r[c])),
      vizType: 'scatter',
      vizData: rows.map(r => ({ label: r.nom, x: r.valeur_M, y: r.buts, color: C.purple })),
      vizLabel: 'Valeur vs Buts',
    }
  }

  if (q.includes('FROM JOUEURS')) {
    let rows = [...DB.joueurs]
    if (q.includes('ORDER BY BUTS'))   rows.sort((a,b) => b.buts - a.buts)
    if (q.includes('ORDER BY VALEUR')) rows.sort((a,b) => b.valeur_M - a.valeur_M)
    if (q.includes('WHERE POSTE')) {
      const m = query.match(/poste\s*=\s*'([^']+)'/i)
      if (m) rows = rows.filter(r => r.poste === m[1].toUpperCase())
    }
    if (q.includes('LIMIT')) { const n = parseInt(q.match(/LIMIT (\d+)/)?.[1] || 10); rows = rows.slice(0, n) }
    const cols = ['nom','poste','buts','passes_d','minutes','valeur_M']
    return {
      columns: cols,
      rows: rows.map(r => cols.map(c => r[c])),
      vizType: 'bar',
      vizData: rows.slice(0,8).map(r => ({ label: r.nom, value: r.buts, color: C.blue })),
      vizLabel: 'Buts',
    }
  }

  if (q.includes('GROUP BY') && q.includes('BUDGET')) {
    const rows = [...DB.clubs].sort((a,b) => b.budget_M - a.budget_M)
    const cols = ['nom_club','budget_M']
    return {
      columns: cols,
      rows: rows.map(r => [r.nom_club, r.budget_M + ' M€']),
      vizType: 'bar',
      vizData: rows.map(r => ({ label: r.nom_club, value: r.budget_M, color: C.gold })),
      vizLabel: 'Budget (M€)',
    }
  }

  if (q.includes('FROM TRANSFERTS')) {
    const joined = DB.transferts.map(t => ({
      joueur: joueurName(t.id_joueur), dep: clubName(t.id_club_dep),
      arr: clubName(t.id_club_arr), montant: t.montant_M, annee: t.annee,
    })).sort((a,b) => b.montant - a.montant)
    return {
      columns: ['joueur','dep','arr','montant_M€','annee'],
      rows: joined.map(r => [r.joueur, r.dep, r.arr, r.montant, r.annee]),
      vizType: 'bar',
      vizData: joined.map(r => ({ label: r.joueur, value: r.montant, color: C.orange })),
      vizLabel: 'Montant (M€)',
    }
  }

  if (q.includes('FROM MATCHS')) {
    return {
      columns: ['dom','ext','score'],
      rows: DB.matchs.map(m => [clubName(m.id_club_dom), clubName(m.id_club_ext), `${m.score_dom} - ${m.score_ext}`]),
      vizType: 'none',
    }
  }

  return { error: `Erreur SQL : table ou syntaxe non reconnue.\nTables disponibles : clubs, joueurs, matchs, transferts` }
}

// ── Chart canvas ──────────────────────────────────────────────────────────────
function SQLChartCanvas({ vizData, vizType, vizLabel, animate }) {
  const ref    = useRef(null)
  const animRef = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !vizData?.length || vizType === 'none') return
    const parent = canvas.parentElement
    const cssW = parent?.clientWidth  || 340
    const cssH = parent?.clientHeight || 180
    const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)

    let progress = animate ? 0 : 1

    const draw = () => {
      ctx.clearRect(0, 0, W, H)
      progress = Math.min(1, progress + 0.045)
      const ease = 1 - Math.pow(1 - progress, 3)

      if (vizType === 'bar') {
        const pad = { l:40, r:12, t:18, b:32 }
        const bw = (W - pad.l - pad.r) / vizData.length
        const maxV = Math.max(...vizData.map(d => d.value)) || 1

        for (let i = 0; i <= 4; i++) {
          const y = pad.t + (H - pad.t - pad.b) * (i / 4)
          ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y)
          ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1; ctx.stroke()
          ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.font = '9px "DM Mono",monospace'
          ctx.textAlign = 'right'
          ctx.fillText(Math.round(maxV * (1 - i / 4)), pad.l - 4, y + 3)
        }

        vizData.forEach((d, i) => {
          const x = pad.l + i * bw + bw * 0.1
          const bWidth = bw * 0.8
          const maxH = H - pad.t - pad.b
          const bHeight = (d.value / maxV) * maxH * ease

          ctx.save()
          ctx.shadowColor = rgba(d.color, 0.5); ctx.shadowBlur = 8
          const grd = ctx.createLinearGradient(x, H - pad.b - bHeight, x, H - pad.b)
          grd.addColorStop(0, rgba(d.color, 0.9)); grd.addColorStop(1, rgba(d.color, 0.25))
          ctx.fillStyle = grd
          ctx.beginPath()
          const br = 3
          ctx.moveTo(x + br, H - pad.b - bHeight)
          ctx.arcTo(x + bWidth, H - pad.b - bHeight, x + bWidth, H - pad.b, br)
          ctx.arcTo(x + bWidth, H - pad.b, x, H - pad.b, 0)
          ctx.arcTo(x, H - pad.b, x, H - pad.b - bHeight, 0)
          ctx.arcTo(x, H - pad.b - bHeight, x + bWidth, H - pad.b - bHeight, br)
          ctx.closePath(); ctx.fill()
          ctx.restore()

          ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.font = '8px "DM Mono",monospace'; ctx.textAlign = 'center'
          const label = d.label.length > 7 ? d.label.slice(0, 6) + '…' : d.label
          ctx.fillText(label, x + bWidth / 2, H - pad.b + 11)

          if (progress > 0.7) {
            ctx.fillStyle = rgba(d.color, (progress - 0.7) / 0.3)
            ctx.font = 'bold 9px "DM Mono",monospace'
            ctx.fillText(d.value, x + bWidth / 2, H - pad.b - bHeight - 5)
          }
        })

        ctx.save(); ctx.translate(10, H / 2); ctx.rotate(-Math.PI / 2)
        ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.font = '9px "DM Mono",monospace'; ctx.textAlign = 'center'
        ctx.fillText(vizLabel, 0, 0); ctx.restore()

      } else if (vizType === 'scatter') {
        const pad = { l:44, r:16, t:18, b:32 }
        const maxX = Math.max(...vizData.map(d => d.x)) || 1
        const maxY = Math.max(...vizData.map(d => d.y)) || 1

        for (let i = 0; i <= 4; i++) {
          const gx = pad.l + (W - pad.l - pad.r) * (i / 4)
          const gy = pad.t + (H - pad.t - pad.b) * (i / 4)
          ctx.beginPath(); ctx.moveTo(gx, pad.t); ctx.lineTo(gx, H - pad.b)
          ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1; ctx.stroke()
          ctx.beginPath(); ctx.moveTo(pad.l, gy); ctx.lineTo(W - pad.r, gy); ctx.stroke()
          ctx.fillStyle = 'rgba(255,255,255,0.25)'; ctx.font = '8px "DM Mono",monospace'
          ctx.textAlign = 'center'
          ctx.fillText(Math.round(maxX * (i / 4)), gx, H - pad.b + 11)
          ctx.textAlign = 'right'
          ctx.fillText(Math.round(maxY * (1 - i / 4)), pad.l - 4, gy + 3)
        }

        vizData.forEach(d => {
          const x = pad.l + (d.x / maxX) * (W - pad.l - pad.r)
          const y = pad.t + (1 - (d.y / maxY) * ease) * (H - pad.t - pad.b)
          ctx.save()
          ctx.shadowColor = rgba(d.color, 0.8); ctx.shadowBlur = 12
          ctx.beginPath(); ctx.arc(x, y, 5.5, 0, Math.PI * 2)
          ctx.fillStyle = rgba(d.color, 0.9); ctx.fill()
          ctx.restore()
          ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = '8px "DM Mono",monospace'; ctx.textAlign = 'left'
          ctx.fillText(d.label.split(' ').pop(), x + 8, y + 3)
        })

        ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.font = '9px "DM Mono",monospace'
        ctx.textAlign = 'center'; ctx.fillText('Valeur (M€)', pad.l + (W - pad.l - pad.r) / 2, H - 2)
        ctx.save(); ctx.translate(10, H / 2); ctx.rotate(-Math.PI / 2)
        ctx.fillText('Buts', 0, 0); ctx.restore()
      }

      if (progress < 1) animRef.current = requestAnimationFrame(draw)
    }
    cancelAnimationFrame(animRef.current)
    draw()
    return () => cancelAnimationFrame(animRef.current)
  }, [vizData, vizType, vizLabel, animate])

  if (!vizData?.length || vizType === 'none') return null
  return <canvas ref={ref} style={{ display:'block', width:'100%', height:'100%' }} />
}

// ── Preset queries ────────────────────────────────────────────────────────────
const SQL_PRESETS = [
  { label:'🏆 Classement',   query:`SELECT nom_club, pts, j, g, n, p, bp, bc\nFROM clubs\nORDER BY pts DESC\nLIMIT 10;` },
  { label:'⚽ Buteurs',      query:`SELECT nom, poste, buts, passes_d, minutes\nFROM joueurs\nORDER BY buts DESC\nLIMIT 10;` },
  { label:'🔗 JOIN joueurs', query:`SELECT j.nom, j.poste, j.buts, j.valeur_M, c.nom_club\nFROM joueurs j\nJOIN clubs c ON j.id_club = c.id_club\nORDER BY j.buts DESC;` },
  { label:'💰 Transferts',   query:`SELECT joueur, id_club_dep, id_club_arr, montant_M, annee\nFROM transferts\nORDER BY montant_M DESC;` },
  { label:'🏟️ Budgets',      query:`SELECT nom_club, budget_M\nFROM clubs\nGROUP BY nom_club\nORDER BY budget_M DESC;` },
  { label:'📅 Matchs',       query:`SELECT id_club_dom, id_club_ext, score_dom, score_ext\nFROM matchs\nORDER BY id_match DESC;` },
]

// ── SQLLiveCanvas main component ──────────────────────────────────────────────
function SQLLiveCanvas({ active }) {
  const [query, setQuery]         = useState(SQL_PRESETS[0].query)
  const [result, setResult]       = useState(null)
  const [running, setRunning]     = useState(false)
  const [scanLine, setScanLine]   = useState(0)
  const [rowsShown, setRowsShown] = useState(0)
  const [showChart, setShowChart] = useState(false)
  const [chartKey, setChartKey]   = useState(0)
  const scanRef = useRef(null)
  const rowRef  = useRef(null)

  const executeQuery = useCallback((q = query) => {
    setRunning(true); setResult(null); setRowsShown(0); setShowChart(false); setScanLine(0)
    let line = 0
    const lines = (q.match(/\n/g) || []).length + 1
    clearInterval(scanRef.current)
    scanRef.current = setInterval(() => {
      line++; setScanLine(line)
      if (line >= lines) {
        clearInterval(scanRef.current)
        setTimeout(() => {
          const res = runSQL(q)
          setResult(res); setRunning(false)
          if (!res.error) {
            let r = 0
            clearInterval(rowRef.current)
            rowRef.current = setInterval(() => {
              r++; setRowsShown(r)
              if (r >= (res.rows?.length || 0)) {
                clearInterval(rowRef.current)
                setTimeout(() => { setShowChart(true); setChartKey(k => k + 1) }, 200)
              }
            }, 55)
          }
        }, 180)
      }
    }, 90)
  }, [query])

  useEffect(() => {
    if (active) setTimeout(() => executeQuery(SQL_PRESETS[0].query), 400)
    return () => { clearInterval(scanRef.current); clearInterval(rowRef.current) }
  }, [active, executeQuery])

  const queryLines = query.split('\n')

  const sqlStyles = {
    root:     { display:'flex', gap:0, height:'100%', width:'100%', fontFamily:'"DM Mono",monospace', fontSize:12, color:'rgba(215,232,224,0.85)' },
    left:     { width:'44%', minWidth:240, display:'flex', flexDirection:'column', gap:8, padding:'12px 0 12px 12px', boxSizing:'border-box', borderRight:'1px solid rgba(255,255,255,0.06)' },
    right:    { flex:1, display:'flex', flexDirection:'column', gap:10, padding:'12px', boxSizing:'border-box', overflowY:'auto' },
    presetBar:{ display:'flex', flexWrap:'wrap', gap:5 },
    presetBtn:{ background:'rgba(0,255,136,0.06)', border:'1px solid rgba(0,255,136,0.18)', borderRadius:100, color:'rgba(0,255,136,0.8)', fontFamily:'"DM Mono",monospace', fontSize:10, padding:'4px 10px', cursor:'pointer', whiteSpace:'nowrap' },
    editorWrap:{ background:'rgba(7,12,10,0.96)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, overflow:'hidden', flex:1 },
    editorHeader:{ display:'flex', alignItems:'center', gap:6, padding:'8px 12px', borderBottom:'1px solid rgba(255,255,255,0.06)', background:'rgba(0,0,0,0.3)' },
    editorDot: col => ({ width:10, height:10, borderRadius:'50%', background:rgba(col,0.7) }),
    editorTitle:{ color:'rgba(255,255,255,0.4)', fontSize:10, marginLeft:4, flex:1 },
    dbBadge:  { fontSize:9, padding:'2px 8px', borderRadius:100, background:'rgba(0,255,136,0.08)', border:'1px solid rgba(0,255,136,0.2)', color:'rgba(0,255,136,0.7)' },
    editorBody:{ display:'flex', minHeight:130 },
    lineNums: { padding:'10px 0', minWidth:32, display:'flex', flexDirection:'column', alignItems:'flex-end', paddingRight:8, background:'rgba(0,0,0,0.2)', userSelect:'none' },
    lineNum:  (active, isCurrent) => ({ height:20, lineHeight:'20px', fontSize:10, width:'100%', textAlign:'right', paddingRight:4, transition:'color 0.1s', color: active ? rgba(C.green,0.9) : 'rgba(255,255,255,0.18)', background: isCurrent ? rgba(C.green,0.07) : 'transparent' }),
    textarea: { flex:1, background:'transparent', border:'none', outline:'none', resize:'none', fontFamily:'"DM Mono",monospace', fontSize:12, color:'rgba(215,232,224,0.9)', lineHeight:'20px', padding:'10px 12px', caretColor:rgba(C.green,1), minHeight:130 },
    editorFooter:{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 12px', borderTop:'1px solid rgba(255,255,255,0.06)', background:'rgba(0,0,0,0.2)' },
    hint:     { fontSize:9, color:'rgba(255,255,255,0.22)' },
    runBtn:   (dis) => ({ display:'flex', alignItems:'center', gap:6, background:rgba(C.green,0.12), border:`1px solid ${rgba(C.green,0.35)}`, borderRadius:100, color:rgba(C.green,1), fontFamily:'"DM Mono",monospace', fontSize:11, fontWeight:700, padding:'5px 14px', cursor:'pointer', opacity: dis ? 0.6 : 1 }),
    spinner:  { display:'inline-block', width:10, height:10, borderRadius:'50%', border:`2px solid ${rgba(C.green,0.3)}`, borderTopColor:rgba(C.green,1), animation:'sqlSpin 0.6s linear infinite' },
    schema:   { background:'rgba(0,0,0,0.25)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 12px' },
    schemaTitle:{ fontSize:9, color:'rgba(255,255,255,0.3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:7 },
    schemaRow:{ marginBottom:5, lineHeight:1.5 },
    schemaName: col => ({ fontWeight:700, fontSize:10, marginRight:6, color:rgba(col,0.9) }),
    schemaCols:{ fontSize:9, color:'rgba(255,255,255,0.28)' },
    placeholder:{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10, opacity:0.3 },
    scanning: { display:'flex', flexDirection:'column', gap:8, padding:'20px 0' },
    scanBar:  { height:3, background:'rgba(255,255,255,0.08)', borderRadius:100, overflow:'hidden' },
    scanFill: (pct) => ({ height:'100%', background:`linear-gradient(90deg,${rgba(C.green,0.6)},${rgba(C.blue,0.8)})`, borderRadius:100, width:`${pct}%`, transition:'width 0.08s' }),
    scanLabel:{ fontSize:10, color:rgba(C.green,0.7) },
    errorBox: { display:'flex', gap:10, padding:14, background:'rgba(255,75,75,0.08)', border:'1px solid rgba(255,75,75,0.22)', borderRadius:8 },
    errorText:{ color:rgba(C.red,0.8), fontSize:11, margin:0, whiteSpace:'pre-wrap' },
    statsBar: { display:'flex', gap:8, flexWrap:'wrap' },
    statChip: col => ({ fontSize:10, padding:'3px 10px', borderRadius:100, background:rgba(col,0.08), border:`1px solid ${rgba(col,0.22)}`, color:rgba(col,0.9) }),
    tableWrap:{ overflowX:'auto' },
    table:    { width:'100%', borderCollapse:'collapse', fontSize:11 },
    th:       { padding:'6px 10px', textAlign:'left', fontSize:9, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.35)', borderBottom:'1px solid rgba(255,255,255,0.08)', background:'rgba(0,0,0,0.2)', whiteSpace:'nowrap' },
    td:       { padding:'5px 10px', fontSize:11, borderBottom:'1px solid rgba(255,255,255,0.04)', whiteSpace:'nowrap', color:'rgba(215,232,224,0.8)' },
    chartWrap:{ background:'rgba(0,0,0,0.2)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, padding:'10px 12px' },
    chartTitle:{ display:'flex', alignItems:'center', gap:7, marginBottom:8, fontSize:10, color:'rgba(255,255,255,0.5)' },
    chartDot: { width:6, height:6, borderRadius:'50%', background:rgba(C.green,1), boxShadow:`0 0 6px ${rgba(C.green,0.8)}`, flexShrink:0 },
    chartType:{ marginLeft:'auto', fontSize:9, color:'rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.05)', padding:'2px 8px', borderRadius:100 },
    chartCanvas:{ height:180, width:'100%' },
  }

  return (
    <div style={sqlStyles.root}>
      <style>{`@keyframes sqlSpin { to { transform:rotate(360deg) } } @keyframes sqlRowIn { from { opacity:0; transform:translateX(-6px) } to { opacity:1; transform:translateX(0) } }`}</style>

      {/* Left — editor */}
      <div style={sqlStyles.left}>
        <div style={sqlStyles.presetBar}>
          {SQL_PRESETS.map((p, i) => (
            <button key={i} style={sqlStyles.presetBtn}
              onClick={() => { setQuery(p.query); setTimeout(() => executeQuery(p.query), 0) }}>
              {p.label}
            </button>
          ))}
        </div>

        <div style={sqlStyles.editorWrap}>
          <div style={sqlStyles.editorHeader}>
            <span style={sqlStyles.editorDot(C.red)} />
            <span style={sqlStyles.editorDot(C.gold)} />
            <span style={sqlStyles.editorDot(C.green)} />
            <span style={sqlStyles.editorTitle}>query.sql</span>
            <span style={sqlStyles.dbBadge}>⬡ football_db</span>
          </div>
          <div style={sqlStyles.editorBody}>
            <div style={sqlStyles.lineNums}>
              {queryLines.map((_, i) => (
                <div key={i} style={sqlStyles.lineNum(running && i < scanLine, running && i === scanLine - 1)}>
                  {running && i < scanLine ? '▶' : i + 1}
                </div>
              ))}
            </div>
            <textarea
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') executeQuery() }}
              spellCheck={false}
              style={sqlStyles.textarea}
            />
          </div>
          <div style={sqlStyles.editorFooter}>
            <span style={sqlStyles.hint}>Ctrl+Enter pour exécuter</span>
            <button onClick={() => executeQuery()} disabled={running} style={sqlStyles.runBtn(running)}>
              {running ? <><span style={sqlStyles.spinner} /> Exécution…</> : <>▶ Exécuter</>}
            </button>
          </div>
        </div>

        <div style={sqlStyles.schema}>
          <div style={sqlStyles.schemaTitle}>Tables disponibles</div>
          {[
            { name:'clubs',      cols:'id_club, nom_club, ville, pts, j, g, n, p, bp, bc, budget_M', col:C.green },
            { name:'joueurs',    cols:'id_joueur, nom, poste, buts, passes_d, minutes, valeur_M, id_club', col:C.blue },
            { name:'matchs',     cols:'id_match, id_club_dom, id_club_ext, score_dom, score_ext', col:C.orange },
            { name:'transferts', cols:'id_transfert, id_joueur, id_club_dep, id_club_arr, montant_M, annee', col:C.gold },
          ].map(t => (
            <div key={t.name} style={sqlStyles.schemaRow}>
              <span style={sqlStyles.schemaName(t.col)}>{t.name}</span>
              <span style={sqlStyles.schemaCols}>{t.cols}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right — results */}
      <div style={sqlStyles.right}>
        {!result && !running && (
          <div style={sqlStyles.placeholder}>
            <div style={{ fontSize:32, color:rgba(C.green,0.4) }}>⬡</div>
            <div style={{ fontSize:11 }}>Lance une requête pour voir les résultats</div>
          </div>
        )}

        {running && (
          <div style={sqlStyles.scanning}>
            <div style={sqlStyles.scanBar}>
              <div style={sqlStyles.scanFill((scanLine / queryLines.length) * 100)} />
            </div>
            <span style={sqlStyles.scanLabel}>Parsing SQL… ligne {scanLine}/{queryLines.length}</span>
          </div>
        )}

        {result?.error && (
          <div style={sqlStyles.errorBox}>
            <span style={{ color:rgba(C.red,0.9), fontSize:14 }}>✕</span>
            <pre style={sqlStyles.errorText}>{result.error}</pre>
          </div>
        )}

        {result && !result.error && (
          <>
            <div style={sqlStyles.statsBar}>
              <span style={sqlStyles.statChip(C.green)}>✓ {result.rows.length} lignes</span>
              <span style={sqlStyles.statChip(C.blue)}>{result.columns.length} colonnes</span>
              <span style={sqlStyles.statChip(C.purple)}>~{(Math.random() * 2 + 0.8).toFixed(1)} ms</span>
            </div>

            <div style={sqlStyles.tableWrap}>
              <table style={sqlStyles.table}>
                <thead>
                  <tr>{result.columns.map(col => <th key={col} style={sqlStyles.th}>{col}</th>)}</tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, rowsShown).map((row, ri) => (
                    <tr key={ri} style={{ background: ri % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', animation:'sqlRowIn 0.2s ease-out' }}>
                      {row.map((cell, ci) => <td key={ci} style={sqlStyles.td}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {result.vizType !== 'none' && (
              <div style={sqlStyles.chartWrap}>
                <div style={sqlStyles.chartTitle}>
                  <span style={sqlStyles.chartDot} />
                  {result.vizLabel}
                  <span style={sqlStyles.chartType}>{result.vizType === 'scatter' ? 'scatter plot' : 'bar chart'}</span>
                </div>
                <div style={sqlStyles.chartCanvas}>
                  <SQLChartCanvas key={chartKey} vizData={result.vizData} vizType={result.vizType} vizLabel={result.vizLabel} animate={showChart} />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  ML PREDICTION — Régression logistique JS pur, zéro dépendance
// ─────────────────────────────────────────────────────────────────────────────

// ── Feature engineering depuis DB.clubs + DB.matchs ──────────────────────────
function buildTrainingData() {
  // Pour chaque match, on crée un sample avec 5 features normalisées + label
  // label : 1 = dom gagne, 0 = nul/ext
  const maxPts = Math.max(...DB.clubs.map(c => c.pts))
  const maxBp  = Math.max(...DB.clubs.map(c => c.bp))
  const maxBdg = Math.max(...DB.clubs.map(c => c.budget_M))

  const feat = (dom, ext) => [
    (dom.pts  - ext.pts)  / maxPts,           // diff points normalisée
    (dom.bp   - ext.bp)   / maxBp,            // diff buts marqués
    (dom.bc   - ext.bc)   / maxBp,            // diff buts concédés (inversé)
    (dom.budget_M - ext.budget_M) / maxBdg,   // diff budget
    0.12,                                      // avantage domicile fixe
  ]

  // Matchs réels comme base
  const samples = DB.matchs.map(m => {
    const dom = DB.clubs.find(c => c.id_club === m.id_club_dom)
    const ext = DB.clubs.find(c => c.id_club === m.id_club_ext)
    const label = m.score_dom > m.score_ext ? 1 : 0
    return { x: feat(dom, ext), y: label }
  })

  // On augmente avec des matchs synthétiques cohérents avec les stats
  DB.clubs.forEach(dom => {
    DB.clubs.forEach(ext => {
      if (dom.id_club === ext.id_club) return
      // label probabiliste basé sur l'écart de pts
      const diff = (dom.pts - ext.pts) / maxPts
      const prob = 1 / (1 + Math.exp(-diff * 6 - 0.5)) // sigmoid biaisée domicile
      const label = Math.random() < prob ? 1 : 0
      samples.push({ x: feat(dom, ext), y: label })
    })
  })
  return samples
}

// ── Logistic regression (gradient descent) ───────────────────────────────────
function sigmoid(z) { return 1 / (1 + Math.exp(-z)) }

function trainLogisticRegression(samples, epochs = 300, lr = 0.08) {
  const nFeat = samples[0].x.length
  const w = new Array(nFeat).fill(0).map(() => (Math.random() - 0.5) * 0.1)
  let b = 0
  const lossHistory = []

  for (let ep = 0; ep < epochs; ep++) {
    let loss = 0
    const dw = new Array(nFeat).fill(0)
    let db = 0

    for (const s of samples) {
      const z   = s.x.reduce((acc, xi, i) => acc + w[i] * xi, b)
      const yHat = sigmoid(z)
      const err  = yHat - s.y
      loss += -(s.y * Math.log(yHat + 1e-9) + (1 - s.y) * Math.log(1 - yHat + 1e-9))
      s.x.forEach((xi, i) => { dw[i] += err * xi })
      db += err
    }

    const n = samples.length
    w.forEach((_, i) => { w[i] -= lr * dw[i] / n })
    b -= lr * db / n
    if (ep % 10 === 0) lossHistory.push(loss / n)
  }
  return { w, b, lossHistory }
}

function predict(model, dom, ext) {
  const maxPts = Math.max(...DB.clubs.map(c => c.pts))
  const maxBp  = Math.max(...DB.clubs.map(c => c.bp))
  const maxBdg = Math.max(...DB.clubs.map(c => c.budget_M))
  const x = [
    (dom.pts  - ext.pts)  / maxPts,
    (dom.bp   - ext.bp)   / maxBp,
    (dom.bc   - ext.bc)   / maxBp,
    (dom.budget_M - ext.budget_M) / maxBdg,
    0.12,
  ]
  const z = x.reduce((acc, xi, i) => acc + model.w[i] * xi, model.b)
  const pDom = sigmoid(z)
  const pExt = 1 - pDom
  // Nul estimé à partir de l'incertitude
  const uncertainty = 1 - Math.abs(pDom - 0.5) * 2
  const nul = uncertainty * 0.35
  const domAdj = pDom * (1 - nul)
  const extAdj = pExt * (1 - nul)
  return { dom: domAdj, nul, ext: extAdj, raw: pDom }
}

// ── Loss curve canvas ─────────────────────────────────────────────────────────
function LossCanvas({ lossHistory }) {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !lossHistory?.length) return
    const cssW = canvas.parentElement?.clientWidth || 300
    const cssH = 70
    const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)
    const pad = { l:32, r:10, t:8, b:18 }
    const maxL = Math.max(...lossHistory)
    const minL = Math.min(...lossHistory)

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1
    for (let i = 0; i <= 3; i++) {
      const y = pad.t + (H - pad.t - pad.b) * (i / 3)
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke()
      ctx.fillStyle = 'rgba(255,255,255,0.2)'; ctx.font = '8px "DM Mono",monospace'; ctx.textAlign = 'right'
      ctx.fillText((minL + (maxL - minL) * (1 - i / 3)).toFixed(2), pad.l - 3, y + 3)
    }

    // Curve fill
    ctx.beginPath()
    lossHistory.forEach((v, i) => {
      const x = pad.l + (i / (lossHistory.length - 1)) * (W - pad.l - pad.r)
      const y = pad.t + (1 - (v - minL) / (maxL - minL || 1)) * (H - pad.t - pad.b)
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    })
    ctx.lineTo(W - pad.r, H - pad.b); ctx.lineTo(pad.l, H - pad.b); ctx.closePath()
    const g = ctx.createLinearGradient(0, pad.t, 0, H - pad.b)
    g.addColorStop(0, rgba(C.purple, 0.3)); g.addColorStop(1, rgba(C.purple, 0))
    ctx.fillStyle = g; ctx.fill()

    // Line
    ctx.beginPath()
    lossHistory.forEach((v, i) => {
      const x = pad.l + (i / (lossHistory.length - 1)) * (W - pad.l - pad.r)
      const y = pad.t + (1 - (v - minL) / (maxL - minL || 1)) * (H - pad.t - pad.b)
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
    })
    ctx.strokeStyle = rgba(C.purple, 0.9); ctx.lineWidth = 1.5; ctx.stroke()

    ctx.fillStyle = 'rgba(255,255,255,0.28)'; ctx.font = '8px "DM Mono",monospace'; ctx.textAlign = 'center'
    ctx.fillText('epochs →', pad.l + (W - pad.l - pad.r) / 2, H - 2)
  }, [lossHistory])

  return <canvas ref={ref} style={{ display:'block', width:'100%' }} />
}

// ── Résultat donut canvas ─────────────────────────────────────────────────────
function PredDonutCanvas({ probs, domName, extName }) {
  const ref = useRef(null)
  const animRef = useRef(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const cssW = canvas.parentElement?.clientWidth || 200
    const cssH = cssW
    const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)
    const cx = W / 2, cy = H / 2, R = Math.min(W, H) * 0.38, r = R * 0.58
    let progress = 0

    const SEGS = [
      { val: probs.dom, col: C.green,  label: domName },
      { val: probs.nul, col: C.gold,   label: 'Nul' },
      { val: probs.ext, col: C.blue,   label: extName },
    ]

    const draw = () => {
      progress = Math.min(1, progress + 0.035)
      const ease = 1 - Math.pow(1 - progress, 3)
      ctx.clearRect(0, 0, W, H)

      let angle = -Math.PI / 2
      SEGS.forEach(seg => {
        const sweep = seg.val * Math.PI * 2 * ease
        ctx.save()
        ctx.shadowColor = rgba(seg.col, 0.5); ctx.shadowBlur = 14
        ctx.beginPath()
        ctx.moveTo(cx, cy)
        ctx.arc(cx, cy, R, angle, angle + sweep)
        ctx.closePath()
        ctx.fillStyle = rgba(seg.col, 0.8); ctx.fill()
        ctx.restore()

        // Segment border
        ctx.beginPath()
        ctx.arc(cx, cy, R, angle, angle + sweep)
        ctx.arc(cx, cy, r, angle + sweep, angle, true)
        ctx.closePath()
        ctx.strokeStyle = 'rgba(7,12,10,0.9)'; ctx.lineWidth = 2; ctx.stroke()

        angle += sweep
      })

      // Inner hole
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(7,12,10,0.95)'; ctx.fill()

      // Center text
      const best = SEGS.reduce((a, b) => a.val > b.val ? a : b)
      ctx.fillStyle = rgba(best.col, 1); ctx.font = `bold ${Math.round(W * 0.11)}px "DM Mono",monospace`; ctx.textAlign = 'center'
      ctx.fillText(`${Math.round(best.val * 100 * ease)}%`, cx, cy + 4)
      ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.font = `${Math.round(W * 0.07)}px "DM Mono",monospace`
      ctx.fillText(best.label.slice(0, 10), cx, cy + Math.round(W * 0.11) + 2)

      if (progress < 1) animRef.current = requestAnimationFrame(draw)
    }
    cancelAnimationFrame(animRef.current)
    draw()
    return () => cancelAnimationFrame(animRef.current)
  }, [probs, domName, extName])

  return <canvas ref={ref} style={{ display:'block', width:'100%', aspectRatio:'1' }} />
}

// ── MLPredCanvas main component ───────────────────────────────────────────────
function MLPredCanvas({ active }) {
  const [phase, setPhase]       = useState('idle')   // idle | training | done
  const [epoch, setEpoch]       = useState(0)
  const [model, setModel]       = useState(null)
  const [lossHist, setLossHist] = useState([])
  const [domId, setDomId]       = useState(1)
  const [extId, setExtId]       = useState(2)
  const [probs, setProbs]       = useState(null)
  const [predKey, setPredKey]   = useState(0)
  const trainRef = useRef(null)

  // Reset quand on quitte l'onglet
  useEffect(() => { if (!active) { setPhase('idle'); setModel(null); setLossHist([]); setProbs(null) } }, [active])

  const startTraining = () => {
    setPhase('training'); setEpoch(0); setLossHist([]); setModel(null); setProbs(null)
    const samples = buildTrainingData()
    const EPOCHS = 300
    let ep = 0
    const partialLoss = []

    // On simule les epochs par batch pour animer la progression
    const step = () => {
      const batchSize = 15
      const end = Math.min(ep + batchSize, EPOCHS)
      // Entraînement partiel
      const res = trainLogisticRegression(samples, end, 0.08)
      ep = end
      // On prend les derniers points de loss
      partialLoss.length = 0
      partialLoss.push(...res.lossHistory)
      setEpoch(ep)
      setLossHist([...res.lossHistory])
      if (ep < EPOCHS) {
        trainRef.current = setTimeout(step, 30)
      } else {
        setModel(res)
        setPhase('done')
      }
    }
    clearTimeout(trainRef.current)
    trainRef.current = setTimeout(step, 50)
  }

  const doPrediction = () => {
    if (!model) return
    const dom = DB.clubs.find(c => c.id_club === domId)
    const ext = DB.clubs.find(c => c.id_club === extId)
    if (!dom || !ext || dom.id_club === ext.id_club) return
    setProbs(predict(model, dom, ext))
    setPredKey(k => k + 1)
  }

  useEffect(() => { return () => clearTimeout(trainRef.current) }, [])

  const dom = DB.clubs.find(c => c.id_club === domId)
  const ext = DB.clubs.find(c => c.id_club === extId)
  const EPOCHS = 300

  const S = {
    root:    { height:'100%', width:'100%', display:'flex', flexDirection:'column', gap:0, fontFamily:'"DM Mono",monospace', color:'rgba(215,232,224,0.85)', overflowY:'auto' },
    top:     { display:'flex', gap:0, flex:1, minHeight:0 },
    left:    { width:'42%', minWidth:220, display:'flex', flexDirection:'column', gap:10, padding:'14px 12px 14px 14px', borderRight:'1px solid rgba(255,255,255,0.06)', boxSizing:'border-box' },
    right:   { flex:1, display:'flex', flexDirection:'column', gap:12, padding:'14px 14px 14px 12px', boxSizing:'border-box', overflowY:'auto' },
    label:   { fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.3)', marginBottom:5 },
    select:  { width:'100%', background:'rgba(7,12,10,0.95)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, color:'rgba(215,232,224,0.9)', fontFamily:'"DM Mono",monospace', fontSize:11, padding:'7px 10px', outline:'none', cursor:'pointer' },
    vsRow:   { display:'flex', alignItems:'center', gap:8 },
    vsLabel: { fontSize:13, color:'rgba(255,255,255,0.2)', fontWeight:700, flexShrink:0 },
    trainBtn:{ width:'100%', padding:'10px', borderRadius:10, border:`1px solid ${rgba(C.purple,0.4)}`, background:rgba(C.purple,0.1), color:rgba(C.purple,1), fontFamily:'"DM Mono",monospace', fontSize:11, fontWeight:700, cursor:'pointer', transition:'all 0.2s' },
    predBtn: { width:'100%', padding:'10px', borderRadius:10, border:`1px solid ${rgba(C.green,0.4)}`, background:rgba(C.green,0.1), color:rgba(C.green,1), fontFamily:'"DM Mono",monospace', fontSize:11, fontWeight:700, cursor:'pointer', transition:'all 0.2s' },
    disabled:{ opacity:0.35, cursor:'not-allowed' },
    progressWrap:{ background:'rgba(255,255,255,0.06)', borderRadius:100, height:4, overflow:'hidden', marginTop:4 },
    progressFill: (pct, col) => ({ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg,${rgba(col,0.7)},${rgba(col,1)})`, borderRadius:100, transition:'width 0.1s' }),
    epochLabel:{ fontSize:9, color:rgba(C.purple,0.7), marginTop:3 },
    featureBox:{ background:'rgba(0,0,0,0.25)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 12px' },
    featureTitle:{ fontSize:9, color:'rgba(255,255,255,0.3)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:8 },
    featureRow:{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 },
    featureLabel:{ fontSize:10, color:'rgba(255,255,255,0.5)' },
    featureBar:{ flex:1, height:4, background:'rgba(255,255,255,0.06)', borderRadius:100, margin:'0 10px', overflow:'hidden' },
    featureBarFill: (val, col) => ({ height:'100%', width:`${Math.abs(val)*100}%`, background:rgba(col,0.8), borderRadius:100, marginLeft: val < 0 ? 'auto' : 0 }),
    featureVal:{ fontSize:10, color:'rgba(255,255,255,0.6)', minWidth:36, textAlign:'right' },
    probRow:  { display:'flex', gap:8, marginBottom:8 },
    probCard: (col) => ({ flex:1, background:rgba(col,0.07), border:`1px solid ${rgba(col,0.22)}`, borderRadius:10, padding:'10px 8px', textAlign:'center' }),
    probPct:  (col) => ({ fontSize:22, fontWeight:800, color:rgba(col,1), display:'block', lineHeight:1 }),
    probLbl:  { fontSize:9, color:'rgba(255,255,255,0.4)', display:'block', marginTop:3, letterSpacing:'0.05em' },
    lossWrap: { background:'rgba(0,0,0,0.2)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:8, padding:'10px 12px' },
    lossTitle:{ fontSize:9, color:rgba(C.purple,0.7), letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:6 },
    donutWrap:{ display:'flex', gap:12, alignItems:'center' },
    donutCanvas:{ width:'45%', maxWidth:160 },
    legend:   { flex:1, display:'flex', flexDirection:'column', gap:6 },
    legendRow:{ display:'flex', alignItems:'center', gap:7 },
    legendDot:(col)=>({ width:8, height:8, borderRadius:'50%', background:rgba(col,1), flexShrink:0 }),
    legendLbl:{ fontSize:10, color:'rgba(255,255,255,0.6)', flex:1 },
    legendPct:(col)=>({ fontSize:12, fontWeight:700, color:rgba(col,0.9) }),
    idle:     { flex:1, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, opacity:0.4 },
  }

  // Features display pour le match sélectionné
  const feats = dom && ext ? (() => {
    const maxPts = Math.max(...DB.clubs.map(c => c.pts))
    const maxBp  = Math.max(...DB.clubs.map(c => c.bp))
    const maxBdg = Math.max(...DB.clubs.map(c => c.budget_M))
    return [
      { label:'Diff. points',  val:(dom.pts - ext.pts) / maxPts,           col:C.green  },
      { label:'Diff. buts +',  val:(dom.bp  - ext.bp)  / maxBp,            col:C.blue   },
      { label:'Diff. buts -',  val:(dom.bc  - ext.bc)  / maxBp,            col:C.orange },
      { label:'Diff. budget',  val:(dom.budget_M - ext.budget_M) / maxBdg, col:C.gold   },
      { label:'Avantage dom.', val:0.12,                                    col:C.teal   },
    ]
  })() : []

  return (
    <div style={S.root}>
      <style>{`@keyframes mlPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
      <div style={S.top}>

        {/* ── LEFT : contrôles ── */}
        <div style={S.left}>
          <div>
            <div style={S.label}>⬡ Modèle</div>
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', lineHeight:1.6, marginBottom:8 }}>
              Régression logistique · 5 features · JS pur · entraînement in-browser
            </div>
          </div>

          {/* Sélecteurs clubs */}
          <div>
            <div style={S.label}>Équipe domicile</div>
            <select style={S.select} value={domId} onChange={e => { setDomId(+e.target.value); setProbs(null) }}>
              {DB.clubs.map(c => <option key={c.id_club} value={c.id_club}>{c.nom_club}</option>)}
            </select>
          </div>
          <div style={S.vsRow}>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.08)' }} />
            <span style={S.vsLabel}>VS</span>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.08)' }} />
          </div>
          <div>
            <div style={S.label}>Équipe extérieur</div>
            <select style={S.select} value={extId} onChange={e => { setExtId(+e.target.value); setProbs(null) }}>
              {DB.clubs.filter(c => c.id_club !== domId).map(c => <option key={c.id_club} value={c.id_club}>{c.nom_club}</option>)}
            </select>
          </div>

          {/* Features visualisées */}
          <div style={S.featureBox}>
            <div style={S.featureTitle}>Features du match</div>
            {feats.map(f => (
              <div key={f.label} style={S.featureRow}>
                <span style={S.featureLabel}>{f.label}</span>
                <div style={S.featureBar}>
                  <div style={S.featureBarFill(f.val, f.col)} />
                </div>
                <span style={S.featureVal}>{f.val >= 0 ? '+' : ''}{(f.val * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>

          {/* Boutons */}
          <button
            style={phase === 'training' ? { ...S.trainBtn, ...S.disabled } : S.trainBtn}
            onClick={startTraining}
            disabled={phase === 'training'}
          >
            {phase === 'training'
              ? <span style={{ animation:'mlPulse 1s infinite' }}>⟳ Entraînement… {epoch}/{EPOCHS}</span>
              : phase === 'done' ? '↺ Ré-entraîner le modèle' : '▶ Entraîner le modèle'}
          </button>

          {/* Progress */}
          {phase === 'training' && (
            <div>
              <div style={S.progressWrap}>
                <div style={S.progressFill((epoch / EPOCHS) * 100, C.purple)} />
              </div>
              <div style={S.epochLabel}>epoch {epoch} / {EPOCHS} · {lossHist.length > 0 ? `loss ${lossHist[lossHist.length-1].toFixed(4)}` : ''}</div>
            </div>
          )}

          <button
            style={phase !== 'done' ? { ...S.predBtn, ...S.disabled } : S.predBtn}
            onClick={doPrediction}
            disabled={phase !== 'done'}
          >
            ✦ Prédire ce match
          </button>
        </div>

        {/* ── RIGHT : résultats ── */}
        <div style={S.right}>
          {phase === 'idle' && (
            <div style={S.idle}>
              <div style={{ fontSize:32 }}>◎</div>
              <div style={{ fontSize:11 }}>Entraîne le modèle pour commencer</div>
            </div>
          )}

          {(phase === 'training' || phase === 'done') && lossHist.length > 1 && (
            <div style={S.lossWrap}>
              <div style={S.lossTitle}>📉 Courbe de Loss (cross-entropy)</div>
              <LossCanvas lossHistory={lossHist} />
            </div>
          )}

          {phase === 'done' && !probs && (
            <div style={{ ...S.idle, opacity:0.35, flex:'none', padding:'20px 0' }}>
              <div style={{ fontSize:11 }}>Modèle prêt · sélectionne un match et clique sur Prédire</div>
            </div>
          )}

          {probs && (
            <>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)', marginBottom:4 }}>
                Résultat prédit · {dom?.nom_club} vs {ext?.nom_club}
              </div>

              {/* Probabilités */}
              <div style={S.probRow}>
                <div style={S.probCard(C.green)}>
                  <span style={S.probPct(C.green)}>{(probs.dom * 100).toFixed(0)}%</span>
                  <span style={S.probLbl}>{dom?.nom_club}</span>
                </div>
                <div style={S.probCard(C.gold)}>
                  <span style={S.probPct(C.gold)}>{(probs.nul * 100).toFixed(0)}%</span>
                  <span style={S.probLbl}>Nul</span>
                </div>
                <div style={S.probCard(C.blue)}>
                  <span style={S.probPct(C.blue)}>{(probs.ext * 100).toFixed(0)}%</span>
                  <span style={S.probLbl}>{ext?.nom_club}</span>
                </div>
              </div>

              {/* Donut */}
              <div style={S.donutWrap}>
                <div style={S.donutCanvas}>
                  <PredDonutCanvas key={predKey} probs={probs} domName={dom?.nom_club} extName={ext?.nom_club} />
                </div>
                <div style={S.legend}>
                  {[
                    { col:C.green,  label:dom?.nom_club, val:probs.dom },
                    { col:C.gold,   label:'Nul',          val:probs.nul },
                    { col:C.blue,   label:ext?.nom_club,  val:probs.ext },
                  ].map(l => (
                    <div key={l.label} style={S.legendRow}>
                      <span style={S.legendDot(l.col)} />
                      <span style={S.legendLbl}>{l.label}</span>
                      <span style={S.legendPct(l.col)}>{(l.val * 100).toFixed(1)}%</span>
                    </div>
                  ))}
                  <div style={{ marginTop:8, fontSize:9, color:'rgba(255,255,255,0.25)', lineHeight:1.6 }}>
                    Confiance : {Math.abs(probs.dom - probs.ext) > 0.3 ? '🟢 Élevée' : Math.abs(probs.dom - probs.ext) > 0.1 ? '🟡 Moyenne' : '🔴 Faible'}
                  </div>
                  <div style={{ fontSize:9, color:'rgba(255,255,255,0.2)', lineHeight:1.6 }}>
                    Données fictives · démo pédagogique
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  K-MEANS CLUSTERING CANVAS
// ─────────────────────────────────────────────────────────────────────────────

// K-Means helpers
function kmAssign(points, centroids) {
  return points.map(p => {
    let best = 0, bestDist = Infinity
    centroids.forEach((c, ci) => {
      const d = Math.hypot(p.x - c.x, p.y - c.y)
      if (d < bestDist) { bestDist = d; best = ci }
    })
    return { ...p, cluster: best }
  })
}
function kmMoveCentroids(points, centroids) {
  return centroids.map((c, ci) => {
    const grp = points.filter(p => p.cluster === ci)
    if (!grp.length) return c
    return { x: grp.reduce((s,p) => s+p.x, 0)/grp.length, y: grp.reduce((s,p) => s+p.y, 0)/grp.length }
  })
}

const CLUSTER_COLS = [C.orange, C.blue, C.green, C.purple]

function KMeansCanvas({ active }) {
  const canvasRef  = useRef(null)
  const animRef    = useRef(null)
  // stateRef holds the "committed" state between steps
  const stateRef   = useRef({ points:[], centroids:[], prevCentroids:[], iteration:0, converged:false, substep:'assign', interpT:0 })
  const [uiState, setUiState] = useState({ phase:'idle', iteration:0, substep:'assign', K:3 })
  const K = uiState.K

  const buildPoints = useCallback((W, H) => {
    const PAD = { l:70, r:30, t:30, b:50 }
    const maxVal  = Math.max(...DB.joueurs.map(j => j.valeur_M))
    const maxButs = Math.max(...DB.joueurs.map(j => j.buts))
    return DB.joueurs.map(j => ({
      x:       PAD.l + (j.valeur_M / maxVal)  * (W - PAD.l - PAD.r),
      y:       PAD.t + (1 - j.buts / maxButs) * (H - PAD.t - PAD.b),
      label:   j.nom,
      valeur:  j.valeur_M,
      buts:    j.buts,
      poste:   j.poste,
      cluster: -1,
    }))
  }, [])

  const launch = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return
    const W = canvas.parentElement?.clientWidth || 800
    const H = canvas.parentElement?.clientHeight || 420
    const points = buildPoints(W, H)
    const shuffled = [...points].sort(() => Math.random() - 0.5)
    const centroids = shuffled.slice(0, K).map(p => ({
      x: p.x + (Math.random()-.5)*60,
      y: p.y + (Math.random()-.5)*60,
    }))
    stateRef.current = { points, centroids, prevCentroids: centroids, iteration:0, converged:false, substep:'assign', interpT:0 }
    setUiState(u => ({ ...u, phase:'running', iteration:0, substep:'assign' }))
  }, [K, buildPoints])

  // Step machine: assign → move → assign → move → ...
  useEffect(() => {
    if (uiState.phase !== 'running') return
    const ASSIGN_DUR = 900   // ms to show assignment
    const MOVE_DUR   = 1100  // ms for centroid travel anim
    let timer

    if (uiState.substep === 'assign') {
      // Immediately assign points to nearest centroid
      const s = stateRef.current
      const assigned = kmAssign(s.points, s.centroids)
      stateRef.current = { ...s, points: assigned }
      // After ASSIGN_DUR, trigger centroid move
      timer = setTimeout(() => {
        const s2 = stateRef.current
        const newC = kmMoveCentroids(s2.points, s2.centroids)
        const converged = newC.every((c,i) => Math.hypot(c.x-s2.centroids[i].x, c.y-s2.centroids[i].y) < 1.5)
        stateRef.current = { ...s2, prevCentroids: [...s2.centroids], centroids: newC, iteration: s2.iteration+1, converged, substep:'move', interpT:0 }
        setUiState(u => ({ ...u, substep:'move', iteration: s2.iteration+1 }))
        if (converged) setUiState(u => ({ ...u, phase:'done', substep:'done' }))
      }, ASSIGN_DUR)
    } else if (uiState.substep === 'move') {
      // Animate centroid travel over MOVE_DUR, then go back to assign
      const start = performance.now()
      const animate = (now) => {
        const t = Math.min(1, (now - start) / MOVE_DUR)
        stateRef.current = { ...stateRef.current, interpT: t }
        if (t < 1) { animRef.current = requestAnimationFrame(animate) }
        else {
          timer = setTimeout(() => {
            setUiState(u => ({ ...u, substep:'assign' }))
          }, 100)
        }
      }
      animRef.current = requestAnimationFrame(animate)
    }
    return () => { clearTimeout(timer); cancelAnimationFrame(animRef.current) }
  }, [uiState.phase, uiState.substep])

  // Canvas draw loop
  useEffect(() => {
    if (!active) return
    const canvas = canvasRef.current; if (!canvas) return

    const init = () => {
      const cssW = canvas.parentElement?.clientWidth  || 800
      const cssH = canvas.parentElement?.clientHeight || 420
      const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)
      if (!stateRef.current.points.length) stateRef.current.points = buildPoints(W, H)
      const PAD = { l:70, r:30, t:30, b:50 }

      const frame = () => {
        ctx.clearRect(0,0,W,H)
        const s = stateRef.current
        const ease = t => 1 - Math.pow(1-t, 3)

        // Draw interpolated centroids
        const drawnCentroids = s.centroids.map((c, ci) => {
          if (!s.prevCentroids[ci] || s.substep !== 'move') return c
          const t = ease(s.interpT)
          return {
            x: s.prevCentroids[ci].x + (c.x - s.prevCentroids[ci].x) * t,
            y: s.prevCentroids[ci].y + (c.y - s.prevCentroids[ci].y) * t,
          }
        })

        // Axes
        ctx.strokeStyle = 'rgba(255,255,255,0.08)'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.moveTo(PAD.l, PAD.t); ctx.lineTo(PAD.l, H-PAD.b); ctx.stroke()
        ctx.beginPath(); ctx.moveTo(PAD.l, H-PAD.b); ctx.lineTo(W-PAD.r, H-PAD.b); ctx.stroke()

        ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.font = '10px "DM Mono",monospace'
        ctx.textAlign = 'center'
        ctx.fillText('Valeur marchande (M€) →', PAD.l + (W-PAD.l-PAD.r)/2, H-6)
        ctx.save(); ctx.translate(14, PAD.t+(H-PAD.t-PAD.b)/2); ctx.rotate(-Math.PI/2)
        ctx.fillText('Buts marqués →', 0, 0); ctx.restore()

        // Grid
        ctx.strokeStyle = 'rgba(255,255,255,0.04)'; ctx.lineWidth = 1
        for (let i=1; i<=4; i++) {
          const x = PAD.l + (W-PAD.l-PAD.r)*(i/4)
          const y = PAD.t + (H-PAD.t-PAD.b)*(i/4)
          ctx.beginPath(); ctx.moveTo(x,PAD.t); ctx.lineTo(x,H-PAD.b); ctx.stroke()
          ctx.beginPath(); ctx.moveTo(PAD.l,y); ctx.lineTo(W-PAD.r,y); ctx.stroke()
        }

        // Cluster zones (glow around centroid)
        drawnCentroids.forEach((c, ci) => {
          const col = CLUSTER_COLS[ci % CLUSTER_COLS.length]
          const grad = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, 120)
          grad.addColorStop(0, rgba(col, 0.09))
          grad.addColorStop(1, rgba(col, 0))
          ctx.beginPath(); ctx.arc(c.x, c.y, 120, 0, Math.PI*2)
          ctx.fillStyle = grad; ctx.fill()
        })

        // Lines from player to centroid — more visible during assign
        s.points.forEach(p => {
          if (p.cluster < 0 || !drawnCentroids[p.cluster]) return
          const c = drawnCentroids[p.cluster]
          const col = CLUSTER_COLS[p.cluster % CLUSTER_COLS.length]
          const alpha = s.substep === 'assign' ? 0.28 : 0.12
          ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(c.x, c.y)
          ctx.strokeStyle = rgba(col, alpha); ctx.lineWidth = 1.5; ctx.stroke()
        })

        // Player dots
        s.points.forEach(p => {
          const col = p.cluster >= 0 ? CLUSTER_COLS[p.cluster % CLUSTER_COLS.length] : [180,180,180]
          ctx.save()
          ctx.shadowColor = rgba(col, 0.8); ctx.shadowBlur = 12
          ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI*2)
          ctx.fillStyle = rgba(col, 0.95); ctx.fill()
          ctx.restore()
          // Poste badge ring
          ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI*2)
          ctx.strokeStyle = 'rgba(255,255,255,0.25)'; ctx.lineWidth = 1; ctx.stroke()

          ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.font = 'bold 9px "DM Mono",monospace'; ctx.textAlign = 'left'
          ctx.fillText(p.label, p.x + 10, p.y - 2)
          ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.font = '8px "DM Mono",monospace'
          ctx.fillText(`${p.buts}⚽ ${p.valeur}M€`, p.x + 10, p.y + 9)
        })

        // Centroid markers — animated travel trail
        drawnCentroids.forEach((c, ci) => {
          const col = CLUSTER_COLS[ci % CLUSTER_COLS.length]
          // Trail line when moving
          if (s.substep === 'move' && s.prevCentroids[ci] && s.interpT < 1) {
            ctx.beginPath()
            ctx.moveTo(s.prevCentroids[ci].x, s.prevCentroids[ci].y)
            ctx.lineTo(c.x, c.y)
            ctx.strokeStyle = rgba(col, 0.35); ctx.lineWidth = 2
            ctx.setLineDash([4,4]); ctx.stroke(); ctx.setLineDash([])
          }
          // Outer pulse ring
          ctx.save()
          ctx.shadowColor = rgba(col, 1); ctx.shadowBlur = 22
          // Diamond ♦
          const sz = 13
          ctx.beginPath()
          ctx.moveTo(c.x, c.y-sz); ctx.lineTo(c.x+sz, c.y)
          ctx.lineTo(c.x, c.y+sz); ctx.lineTo(c.x-sz, c.y)
          ctx.closePath()
          ctx.fillStyle = rgba(col, 0.3); ctx.fill()
          ctx.strokeStyle = rgba(col, 1); ctx.lineWidth = 2.5; ctx.stroke()
          ctx.restore()
          // Label
          ctx.fillStyle = rgba(col, 1); ctx.font = 'bold 10px "DM Mono",monospace'; ctx.textAlign = 'center'
          ctx.fillText(`Centre ${ci+1}`, c.x, c.y - 18)
        })

        // Phase indicator top-right
        if (s.substep === 'assign' && s.iteration > 0) {
          const msg = '📍 Attribution des joueurs au centre le plus proche'
          ctx.fillStyle = rgba(C.gold, 0.8); ctx.font = '9px "DM Mono",monospace'; ctx.textAlign = 'right'
          ctx.fillText(msg, W - 10, 18)
        } else if (s.substep === 'move') {
          const msg = '⟳ Déplacement des centres vers le milieu de leur groupe'
          ctx.fillStyle = rgba(C.purple, 0.8); ctx.font = '9px "DM Mono",monospace'; ctx.textAlign = 'right'
          ctx.fillText(msg, W - 10, 18)
        }

        animRef.current = requestAnimationFrame(frame)
      }
      cancelAnimationFrame(animRef.current); frame()
    }

    const onResize = () => { cancelAnimationFrame(animRef.current); requestAnimationFrame(init) }
    const raf = requestAnimationFrame(init)
    window.addEventListener('resize', onResize)
    return () => { cancelAnimationFrame(animRef.current); cancelAnimationFrame(raf); window.removeEventListener('resize', onResize) }
  }, [active, buildPoints])

  const clusterCounts = stateRef.current.points.reduce((acc, p) => {
    if (p.cluster >= 0) acc[p.cluster] = (acc[p.cluster] || 0) + 1
    return acc
  }, {})

  const F = { fontFamily:'"DM Mono",monospace' }
  const substepLabel = uiState.substep === 'assign'
    ? { txt:'📍 Attribution', col:C.gold }
    : uiState.substep === 'move'
    ? { txt:'⟳ Déplacement',  col:C.purple }
    : { txt:'✓ Convergé',     col:C.green }

  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>

      {/* Top controls bar */}
      <div style={{ position:'absolute', top:8, left:10, zIndex:5, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        {/* K chips */}
        <div style={{ display:'flex', gap:3, alignItems:'center', background:'rgba(0,0,0,0.5)', borderRadius:100, padding:'3px 8px', border:'1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ ...F, fontSize:9, color:'rgba(255,255,255,0.35)', marginRight:2 }}>K groupes</span>
          {[2,3,4].map(k => (
            <button key={k} onClick={() => setUiState(u => ({ ...u, K:k }))} style={{
              width:22, height:22, borderRadius:6,
              border:`1px solid ${K===k ? rgba(C.orange,0.6) : 'rgba(255,255,255,0.1)'}`,
              background: K===k ? rgba(C.orange,0.18) : 'transparent',
              color: K===k ? rgba(C.orange,1) : 'rgba(255,255,255,0.4)',
              ...F, fontSize:11, fontWeight:700, cursor:'pointer',
            }}>{k}</button>
          ))}
        </div>

        <button onClick={launch} style={{
          padding:'5px 14px', borderRadius:100,
          border:`1px solid ${rgba(C.orange,0.45)}`,
          background: rgba(C.orange,0.12), color:rgba(C.orange,1),
          ...F, fontSize:10, fontWeight:700, cursor:'pointer',
        }}>
          {uiState.phase === 'idle' ? '▶ Lancer' : '↺ Relancer'}
        </button>

        {uiState.phase !== 'idle' && (
          <div style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,0,0,0.45)', padding:'4px 10px', borderRadius:100, border:'1px solid rgba(255,255,255,0.07)' }}>
            <span style={{ ...F, fontSize:9, color:rgba(substepLabel.col, 0.9), fontWeight:700 }}>{substepLabel.txt}</span>
            {uiState.phase === 'running' && (
              <span style={{ ...F, fontSize:9, color:'rgba(255,255,255,0.35)' }}>· itér. {uiState.iteration}</span>
            )}
            {uiState.phase === 'done' && (
              <span style={{ ...F, fontSize:9, color:'rgba(255,255,255,0.35)' }}>· en {uiState.iteration} itérations</span>
            )}
          </div>
        )}
      </div>

      {/* Cluster legend top-right */}
      {uiState.phase !== 'idle' && (
        <div style={{ position:'absolute', top:8, right:10, zIndex:5, display:'flex', flexDirection:'column', gap:3 }}>
          {Array.from({ length:K }, (_,i) => {
            const col = CLUSTER_COLS[i % CLUSTER_COLS.length]
            const names = stateRef.current.points.filter(p=>p.cluster===i).map(p=>p.label).join(', ')
            return (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,0,0,0.45)', padding:'3px 8px', borderRadius:100, border:`1px solid ${rgba(col,0.2)}` }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:rgba(col,1), boxShadow:`0 0 5px ${rgba(col,0.7)}` }} />
                <span style={{ ...F, fontSize:8, color:'rgba(255,255,255,0.5)' }}>
                  Groupe {i+1}{names ? ` · ${clusterCounts[i]||0} joueurs` : ''}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Idle state hint */}
      {uiState.phase === 'idle' && (
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', zIndex:3, pointerEvents:'none', gap:10 }}>
          <div style={{ ...F, fontSize:12, color:'rgba(255,255,255,0.25)', letterSpacing:'0.06em' }}>
            Choisis K groupes puis lance l'algorithme
          </div>
          <div style={{ ...F, fontSize:10, color:'rgba(255,255,255,0.15)' }}>
            Les joueurs seront regroupés par profil (valeur + buts)
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="viz-canvas-el" style={{ display:'block' }} />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//  MOTEUR DE RECOMMANDATION — Similarité cosinus
// ─────────────────────────────────────────────────────────────────────────────

function cosineSimilarity(a, b) {
  const dot  = a.reduce((s, v, i) => s + v * b[i], 0)
  const normA = Math.sqrt(a.reduce((s, v) => s + v * v, 0))
  const normB = Math.sqrt(b.reduce((s, v) => s + v * v, 0))
  return normA && normB ? dot / (normA * normB) : 0
}

function getJoueurVector(j) {
  // Normalisation sur les maxima du dataset
  const maxButs   = Math.max(...DB.joueurs.map(x => x.buts))
  const maxPasses = Math.max(...DB.joueurs.map(x => x.passes_d))
  const maxMin    = Math.max(...DB.joueurs.map(x => x.minutes))
  const maxVal    = Math.max(...DB.joueurs.map(x => x.valeur_M))
  return [
    j.buts     / (maxButs   || 1),
    j.passes_d / (maxPasses || 1),
    j.minutes  / (maxMin    || 1),
    j.valeur_M / (maxVal    || 1),
    j.poste === 'ATT' ? 1 : 0,
    j.poste === 'MIL' ? 1 : 0,
    j.poste === 'DEF' ? 1 : 0,
    j.poste === 'GK'  ? 1 : 0,
  ]
}

function RadarCompareCanvas({ joueurA, joueurB }) {
  const ref = useRef(null)
  const animRef = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !joueurA || !joueurB) return
    const cssW = canvas.parentElement?.clientWidth || 260
    const cssH = cssW * 0.85
    const { ctx, W, H } = setupCanvas(canvas, cssW, cssH)

    const AXES = [
      { label:'Buts',    ka:'buts',     max: Math.max(...DB.joueurs.map(x => x.buts))     },
      { label:'Passes',  ka:'passes_d', max: Math.max(...DB.joueurs.map(x => x.passes_d)) },
      { label:'Minutes', ka:'minutes',  max: Math.max(...DB.joueurs.map(x => x.minutes))  },
      { label:'Valeur',  ka:'valeur_M', max: Math.max(...DB.joueurs.map(x => x.valeur_M)) },
    ]
    const N = AXES.length
    const cx = W / 2, cy = H / 2 + 4, R = Math.min(W, H) * 0.32
    const pt = (i, r) => ({
      x: cx + r * Math.cos((i / N) * Math.PI * 2 - Math.PI / 2),
      y: cy + r * Math.sin((i / N) * Math.PI * 2 - Math.PI / 2),
    })
    let progress = 0

    const draw = () => {
      progress = Math.min(1, progress + 0.04)
      const ease = 1 - Math.pow(1 - progress, 3)
      ctx.clearRect(0, 0, W, H)

      // Grid rings
      for (let ring = 1; ring <= 4; ring++) {
        ctx.beginPath()
        for (let i = 0; i <= N; i++) { const p = pt(i % N, (ring / 4) * R); i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) }
        ctx.strokeStyle = `rgba(255,255,255,${ring === 4 ? 0.08 : 0.04})`; ctx.lineWidth = 1; ctx.stroke()
      }
      AXES.forEach((_, i) => {
        const p = pt(i, R)
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(p.x, p.y)
        ctx.strokeStyle = 'rgba(255,255,255,0.06)'; ctx.lineWidth = 1; ctx.stroke()
      })

      // Draw player A
      ctx.beginPath()
      AXES.forEach((ax, i) => { const v = (joueurA[ax.ka] / ax.max) * R * ease; const p = pt(i, v); i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) })
      ctx.closePath()
      ctx.fillStyle = rgba(C.green, 0.18); ctx.fill()
      ctx.strokeStyle = rgba(C.green, 0.85); ctx.lineWidth = 1.5; ctx.stroke()

      // Draw player B
      ctx.beginPath()
      AXES.forEach((ax, i) => { const v = (joueurB[ax.ka] / ax.max) * R * ease; const p = pt(i, v); i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) })
      ctx.closePath()
      ctx.fillStyle = rgba(C.purple, 0.18); ctx.fill()
      ctx.strokeStyle = rgba(C.purple, 0.85); ctx.lineWidth = 1.5; ctx.stroke()

      // Axis labels
      AXES.forEach((ax, i) => {
        const lp = pt(i, R * 1.28)
        ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = '9px "DM Mono",monospace'
        ctx.textAlign = lp.x < cx - 5 ? 'right' : lp.x > cx + 5 ? 'left' : 'center'
        ctx.fillText(ax.label, lp.x, lp.y + 3)
      })

      if (progress < 1) animRef.current = requestAnimationFrame(draw)
    }
    cancelAnimationFrame(animRef.current); draw()
    return () => cancelAnimationFrame(animRef.current)
  }, [joueurA, joueurB])

  return <canvas ref={ref} style={{ display:'block', width:'100%' }} />
}

function RecoCanvas({ active }) {
  const [selectedId, setSelectedId] = useState(DB.joueurs[0].id_joueur)
  const [animKey, setAnimKey]       = useState(0)

  const selected = DB.joueurs.find(j => j.id_joueur === selectedId)
  const selectedVec = selected ? getJoueurVector(selected) : null

  const recommendations = selected
    ? DB.joueurs
        .filter(j => j.id_joueur !== selectedId)
        .map(j => ({
          ...j,
          similarity: cosineSimilarity(selectedVec, getJoueurVector(j)),
          club: DB.clubs.find(c => c.id_club === j.id_club),
        }))
        .sort((a, b) => b.similarity - a.similarity)
    : []

  const handleSelect = (id) => { setSelectedId(id); setAnimKey(k => k + 1) }

  const posteBadge = (poste) => {
    const map = { ATT: C.orange, MIL: C.blue, DEF: C.green, GK: C.purple }
    return map[poste] || C.teal
  }

  const S = {
    root:    { display:'flex', gap:0, height:'100%', width:'100%', fontFamily:'"DM Mono",monospace', fontSize:12, color:'rgba(215,232,224,0.85)' },
    left:    { width:'38%', minWidth:200, display:'flex', flexDirection:'column', gap:0, borderRight:'1px solid rgba(255,255,255,0.06)', overflowY:'auto' },
    right:   { flex:1, display:'flex', flexDirection:'column', gap:0, overflowY:'auto' },
    listHdr: { padding:'12px 14px 8px', fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.3)', borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 },
    joueurRow: (sel) => ({
      display:'flex', alignItems:'center', gap:8, padding:'9px 14px', cursor:'pointer',
      borderBottom:'1px solid rgba(255,255,255,0.04)',
      background: sel ? rgba(C.green,0.07) : 'transparent',
      borderLeft: sel ? `2px solid ${rgba(C.green,0.7)}` : '2px solid transparent',
      transition:'all 0.15s',
    }),
    posteTag: (col) => ({ fontSize:8, padding:'2px 6px', borderRadius:4, background:rgba(col,0.15), border:`1px solid ${rgba(col,0.3)}`, color:rgba(col,0.9), flexShrink:0 }),
    joueurName:{ fontSize:11, flex:1 },
    recoHdr:  { padding:'12px 14px 8px', display:'flex', alignItems:'center', gap:8, borderBottom:'1px solid rgba(255,255,255,0.05)', flexShrink:0 },
    recoTitle:{ fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.3)' },
    selectedBadge:{ fontSize:10, color:rgba(C.green,0.9), fontWeight:700 },
    recoList: { flex:1, overflowY:'auto' },
    recoRow:  (rank) => ({
      display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
      borderBottom:'1px solid rgba(255,255,255,0.04)',
      animation:`recoIn 0.3s ease-out ${rank * 60}ms both`,
    }),
    rank:     (sim) => ({ fontSize:11, fontWeight:700, color: sim > 0.9 ? rgba(C.green,1) : sim > 0.7 ? rgba(C.gold,1) : rgba(C.orange,0.8), minWidth:30 }),
    simBar:   { flex:1, height:3, background:'rgba(255,255,255,0.06)', borderRadius:100, overflow:'hidden' },
    simFill:  (sim) => ({ height:'100%', width:`${sim*100}%`, background:`linear-gradient(90deg,${rgba(C.green,0.7)},${rgba(C.blue,0.9)})`, borderRadius:100, transition:'width 0.6s ease-out' }),
    simPct:   { fontSize:11, fontWeight:700, minWidth:36, textAlign:'right', color:'rgba(255,255,255,0.7)' },
    radar:    { padding:'12px 14px', borderTop:'1px solid rgba(255,255,255,0.06)' },
    radarTitle:{ fontSize:9, color:'rgba(255,255,255,0.3)', letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:8, display:'flex', alignItems:'center', gap:8 },
    radarLeg: { display:'flex', gap:12, marginBottom:6 },
    radarDot: (col) => ({ display:'inline-block', width:8, height:8, borderRadius:'50%', background:rgba(col,1), marginRight:4 }),
  }

  return (
    <div style={S.root}>
      <style>{`
        @keyframes recoIn { from { opacity:0; transform:translateX(-8px) } to { opacity:1; transform:translateX(0) } }
      `}</style>

      {/* Left — joueur selector */}
      <div style={S.left}>
        <div style={S.listHdr}>Sélectionne un joueur</div>
        {DB.joueurs.map(j => {
          const club = DB.clubs.find(c => c.id_club === j.id_club)
          const col = posteBadge(j.poste)
          return (
            <div key={j.id_joueur} style={S.joueurRow(j.id_joueur === selectedId)} onClick={() => handleSelect(j.id_joueur)}>
              <span style={S.posteTag(col)}>{j.poste}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={S.joueurName}>{j.nom}</div>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.3)' }}>{club?.nom_club}</div>
              </div>
              <div style={{ fontSize:9, color:rgba(C.gold,0.8) }}>{j.valeur_M}M€</div>
            </div>
          )
        })}
      </div>

      {/* Right — recommendations */}
      <div style={S.right}>
        <div style={S.recoHdr}>
          <span style={S.recoTitle}>Joueurs similaires à</span>
          <span style={S.selectedBadge}>{selected?.nom}</span>
          <span style={{ marginLeft:'auto', fontSize:9, color:'rgba(255,255,255,0.25)', background:'rgba(255,255,255,0.05)', padding:'2px 8px', borderRadius:100 }}>similarité cosinus</span>
        </div>

        <div style={S.recoList} key={animKey}>
          {recommendations.map((j, ri) => {
            const col = posteBadge(j.poste)
            return (
              <div key={j.id_joueur} style={S.recoRow(ri)}>
                <span style={{ fontSize:9, color:'rgba(255,255,255,0.25)', minWidth:16 }}>#{ri+1}</span>
                <span style={S.posteTag(col)}>{j.poste}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11 }}>{j.nom}</div>
                  <div style={{ fontSize:9, color:'rgba(255,255,255,0.3)' }}>{j.club?.nom_club} · {j.buts}⚽ {j.passes_d}🎯</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:3, minWidth:90, alignItems:'flex-end' }}>
                  <span style={S.rank(j.similarity)}>{(j.similarity * 100).toFixed(1)}%</span>
                  <div style={S.simBar}>
                    <div style={S.simFill(j.similarity)} />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Radar compare — selected vs top-1 */}
        {recommendations.length > 0 && (
          <div style={S.radar}>
            <div style={S.radarTitle}>
              Comparaison radar
              <div style={S.radarLeg}>
                <span><span style={S.radarDot(C.green)} />{selected?.nom}</span>
                <span><span style={S.radarDot(C.purple)} />{recommendations[0]?.nom}</span>
              </div>
            </div>
            <RadarCompareCanvas key={animKey} joueurA={selected} joueurB={recommendations[0]} />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── AI CASES ─────────────────────────────────────────────────────────────────
const AI_CASES = [
  { icon:'◈', color:'#00ff88', title:"A/B Testing IA",      desc:"Analyse automatique des variantes UI. L'IA identifie les éléments qui convertissent le mieux en temps réel.", metric:'+34%', metricLabel:'conversion avg', tags:['Python','Stats','React'] },
  { icon:'◎', color:'#00aaff', title:'Heatmap Prédictive',  desc:'Prédit où les utilisateurs vont cliquer avant même le lancement, via des modèles de comportement.',           metric:'92%',  metricLabel:'précision modèle', tags:['ML','CV','D3.js'] },
  { icon:'⬡', color:'#a78bfa', title:'Chatbot Contextuel',  desc:'Assistants IA sur-mesure entraînés sur vos données métier. Intégration directe dans votre site.',             metric:'-60%', metricLabel:'support humain',   tags:['LLM','RAG','API'] },
  { icon:'✦', color:'#f59e0b', title:'Dashboard Analytics', desc:'Tableaux de bord temps-réel connectés à vos BDD. Visualisations interactives pour vos décisions.',            metric:'∞',    metricLabel:'données live',     tags:['SQL','Charts','Node'] },
]

function AICaseCard({ item, index, visible }) {
  return (
    <div className={`ai-case-card glass-card ${visible ? 'visible' : ''}`}
      style={{ '--color': item.color, '--delay': `${index * 110}ms` }}>
      <div className="ai-case-top">
        <span className="ai-case-icon" style={{ color: item.color }}>{item.icon}</span>
        <div className="ai-case-metric">
          <span className="ai-case-metric-value" style={{ color: item.color }}>{item.metric}</span>
          <span className="ai-case-metric-label">{item.metricLabel}</span>
        </div>
      </div>
      <h4 className="ai-case-title">{item.title}</h4>
      <p className="ai-case-desc">{item.desc}</p>
      <div className="ai-case-tags">
        {item.tags.map(t => <span key={t} className="ai-case-tag" style={{ '--color': item.color }}>{t}</span>)}
      </div>
      <div className="ai-case-bar" />
    </div>
  )
}

// ─── TAB INFO PANEL ───────────────────────────────────────────────────────────
const TAB_INFO = {
  mcd: {
    title: 'MCD — Modèle Conceptuel de Données',
    color: C.teal,
    desc: 'Avant de créer une base de données, on dessine un plan. Le MCD, c\'est ce plan : il montre quelles "tables" existent (clubs, joueurs, matchs…), quelles infos chacune contient, et comment elles sont liées entre elles.',
    analogy: { icon:'🏗️', text:'C\'est comme le plan d\'architecte d\'une maison — on le dessine avant de poser la première brique.' },
    steps: null,
    usages: ['Concevoir une base de données propre', 'Documenter la structure d\'une app', 'Communiquer avec un client avant de coder'],
  },
  stream: {
    title: 'Flux de données en temps réel',
    color: C.green,
    desc: 'Ces courbes se mettent à jour 60 fois par seconde. En vrai projet, les données viendraient d\'un serveur via WebSocket — une connexion ouverte en permanence qui "pousse" les nouvelles valeurs dès qu\'elles arrivent.',
    analogy: { icon:'📡', text:'Comme un commentateur sportif qui parle en direct — pas besoin de recharger la page pour avoir les dernières infos.' },
    steps: null,
    usages: ['Tableau de bord analytics live', 'Monitoring de serveur', 'Suivi de commandes en temps réel'],
  },
  radar: {
    title: 'Graphe Radar — Profil multi-compétences',
    color: C.green,
    desc: 'Un radar permet de voir d\'un seul coup d\'œil un profil sur plusieurs axes. Chaque pointe du polygone représente une compétence. Plus la surface est grande, plus le profil est complet.',
    analogy: { icon:'⚽', text:'Comme la carte d\'un joueur FIFA — tu vois d\'un seul regard si c\'est un spécialiste ou un profil polyvalent.' },
    steps: null,
    usages: ['Profil de compétences', 'Comparaison de produits', 'Évaluation de performance'],
  },
  sql: {
    title: 'SQL Live — Requêtes interactives',
    color: C.gold,
    desc: 'SQL est le langage universel pour interroger une base de données. Tu poses une question en anglais structuré ("SELECT * FROM joueurs") et la base te répond avec les lignes qui correspondent.',
    analogy: { icon:'🔎', text:'C\'est comme une recherche Google, mais ultra-précise : tu peux filtrer, trier, calculer et croiser des millions de lignes en une fraction de seconde.' },
    steps: null,
    usages: ['Extraire des stats précises', 'Générer des rapports automatiques', 'Alimenter un dashboard en temps réel'],
  },
  ml: {
    title: 'Régression Logistique — Prédiction',
    color: C.purple,
    desc: 'C\'est un algorithme qui apprend à partir d\'exemples passés pour prédire un résultat futur. Ici, il analyse les matchs précédents (buts, points, budget…) pour estimer les chances de victoire, nul ou défaite.',
    analogy: { icon:'📊', text:'Comme un parieur expérimenté qui a regardé des centaines de matchs et reconnaît les patterns — sauf que lui ne se trompe jamais pour les mêmes raisons.' },
    steps: null,
    usages: ['Prédiction de résultats sportifs', 'Score de crédit bancaire', 'Détection de spam email'],
    limits: 'Le modèle suppose que les relations entre les variables sont linéaires. Pour des cas plus complexes, on utilise des réseaux de neurones.',
  },
  kmeans: {
    title: 'K-Means — Regrouper les joueurs similaires',
    color: C.orange,
    desc: 'Imagine que tu dois répartir 10 joueurs en groupes sans étiquettes — juste en regardant leurs stats. K-Means fait exactement ça : il trouve automatiquement K groupes de joueurs qui se ressemblent le plus.',
    analogy: { icon:'⚽', text:'C\'est comme un recruteur qui lit des CV sans poste précis : il pile les profils similaires, puis met un nom sur chaque pile à la fin.' },
    steps: [
      { icon:'1', label:'Placement aléatoire', desc:'K "centres de groupe" (les ♦) sont placés aléatoirement sur le graphe.' },
      { icon:'2', label:'Attribution',          desc:'Chaque joueur rejoint le centre ♦ le plus proche — les lignes colorées montrent qui va où.' },
      { icon:'3', label:'Déplacement',          desc:'Chaque centre ♦ se déplace vers le milieu exact de ses joueurs assignés. On voit la traîne pointillée.' },
      { icon:'4', label:'On répète',            desc:'Étapes 2 & 3 en boucle jusqu\'à ce que les centres ne bougent plus = convergence.' },
    ],
    usages: [
      'Segmenter des clients par comportement d\'achat',
      'Regrouper des articles de presse par thème',
      'Identifier des zones géographiques similaires',
      'Compresser une image en réduisant ses couleurs',
    ],
    limits: 'Il faut choisir K à l\'avance. Si les groupes ont des formes très irrégulières, l\'algorithme DBSCAN sera plus adapté.',
  },
  reco: {
    title: 'Recommandation — Trouver les joueurs similaires',
    color: C.blue,
    desc: 'Chaque joueur est décrit par un vecteur de 8 chiffres (buts, passes, minutes, valeur, poste…). La similarité cosinus mesure si deux vecteurs "pointent dans la même direction" — 100% = profils quasi-identiques.',
    analogy: { icon:'🎯', text:'C\'est la même technique qu\'utilise Spotify pour te recommander un artiste : si tes goûts ressemblent à ceux d\'autres utilisateurs, tu vas probablement aimer ce qu\'ils aiment.' },
    steps: null,
    usages: ['Recommandation de produits (Amazon)', 'Suggestion de films (Netflix)', 'Matching de profils (LinkedIn)', 'Recherche de documents similaires'],
  },
}

function InfoPanel({ tabId, onClose }) {
  const info = TAB_INFO[tabId]
  if (!info) return null
  const col = info.color

  return (
    <div className="info-panel-overlay" onClick={onClose}>
      <div className="info-panel glass-card" onClick={e => e.stopPropagation()}
        style={{ '--panel-color': rgba(col, 1), '--panel-color-dim': rgba(col, 0.15), '--panel-border': rgba(col, 0.3) }}>

        {/* Header */}
        <div className="info-panel-header">
          <div className="info-panel-dot" style={{ background: rgba(col, 1), boxShadow: `0 0 10px ${rgba(col, 0.7)}` }} />
          <h4 className="info-panel-title">{info.title}</h4>
          <button className="info-panel-close" onClick={onClose}>✕</button>
        </div>

        {/* Description */}
        <p className="info-panel-desc">{info.desc}</p>

        {/* Analogy */}
        {info.analogy && (
          <div className="info-panel-analogy" style={{ background: rgba(col, 0.07), borderColor: rgba(col, 0.2) }}>
            <span className="info-panel-analogy-icon">{info.analogy.icon}</span>
            <p className="info-panel-analogy-text">{info.analogy.text}</p>
          </div>
        )}

        {/* Steps */}
        {info.steps && (
          <div className="info-panel-steps">
            <div className="info-panel-section-label">Comment ça fonctionne</div>
            {info.steps.map((s, i) => (
              <div key={i} className="info-step">
                <div className="info-step-num" style={{ background: rgba(col, 0.15), border: `1px solid ${rgba(col, 0.4)}`, color: rgba(col, 1) }}>{s.icon}</div>
                <div>
                  <div className="info-step-label">{s.label}</div>
                  <div className="info-step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Limits */}
        {info.limits && (
          <div className="info-panel-limits">
            <div className="info-panel-section-label">Limites & quand utiliser autre chose</div>
            <p className="info-panel-limits-text">{info.limits}</p>
          </div>
        )}

        {/* Use cases */}
        <div className="info-panel-usages">
          <div className="info-panel-section-label">Cas d'usage concrets</div>
          <div className="info-panel-tags">
            {info.usages.map((u, i) => (
              <span key={i} className="info-panel-tag" style={{ background: rgba(col, 0.08), border: `1px solid ${rgba(col, 0.2)}`, color: rgba(col, 0.9) }}>
                {u}
              </span>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function DataIASection() {
  const [sectionRef, visible] = useReveal(0.08)
  const [activeData, setActiveData] = useState('mcd')
  const [activeML,   setActiveML]   = useState('ml')
  const [infoTab,    setInfoTab]    = useState(null)   // id de l'onglet dont le panel est ouvert

  // ── Onglets DATA (visualisation / exploration) ────────────────────────────
  const DATA_TABS = [
    { id:'mcd',    label:'⬡ MCD Football'     },
    { id:'stream', label:'◈ Flux live'         },
    { id:'radar',  label:'◎ Compétences'       },
    { id:'sql',    label:'▶ SQL Live'           },
  ]

  // ── Onglets IA (algorithmes ML) ───────────────────────────────────────────
  const ML_TABS = [
    { id:'ml',     label:'◎ Prédiction ML'      },
    { id:'kmeans', label:'⬡ K-Means'            },
    { id:'reco',   label:'✦ Recommandation'      },
  ]

  const LEG = {
    mcd:    { main:'10 entités · 15 relations · football mondial', sub:'drag · scroll pour zoomer · survol' },
    stream: { main:'Métriques web simulées',                       sub:'mise à jour 60fps' },
    radar:  { main:'Stack Data & IA',                              sub:'auto-évaluation 2026' },
    sql:    { main:'football_db · 4 tables · données fictives',    sub:'Ctrl+Enter pour exécuter' },
    ml:     { main:'Régression logistique · JS pur · in-browser',  sub:'entraînement en temps réel' },
    kmeans: { main:'K-Means clustering · 10 clubs · pts vs buts',  sub:'sélectionne K puis lance' },
    reco:   { main:'Similarité cosinus · 8 features · 10 joueurs', sub:'clique sur un joueur' },
  }

  const renderTabBtn = (t, activeId, setActive, group) => (
    <div key={t.id} className="viz-tab-wrap">
      <button
        className={`viz-tab ${activeId === t.id ? 'active' : ''}`}
        onClick={() => setActive(t.id)}>
        {t.label}
      </button>
      <button
        className="viz-tab-info"
        onClick={e => { e.stopPropagation(); setInfoTab(infoTab === t.id ? null : t.id) }}
        title="En savoir plus"
      >?</button>
    </div>
  )


  return (
    <section id="data-ia" className="section dataia-section" ref={sectionRef}>
      <div className={`section-inner ${visible ? 'visible' : ''}`}>

        <div className="section-label"><span>05</span> Data &amp; IA</div>
        <h2 className="section-title">
          L'IA au service <span className="gradient-text">du web</span>
        </h2>
        <p className="section-desc">
          Combiner développement web et intelligence artificielle pour créer des
          expériences qui apprennent, analysent et s'améliorent en continu.
        </p>

        {/* ══════════════ BLOC DATA ══════════════ */}
        <div className="lab-block">
          <div className="lab-block-header">
            <div className="lab-block-badge lab-block-badge--data">
              <span className="lab-block-badge-dot" />
              Data Lab
            </div>
            <p className="lab-block-desc">Exploration, visualisation et manipulation de données</p>
          </div>

          <div className="dataia-viz-block">
            <div className="viz-tabs">
              {DATA_TABS.map(t => renderTabBtn(t, activeData, setActiveData, 'data'))}
            </div>

            <div className={`viz-canvas-wrap glass-card ${activeData === 'mcd' ? 'mcd-active' : ''} ${activeData === 'sql' ? 'sql-active' : ''}`}>
              <div className="viz-live-badge">
                {activeData === 'mcd' ? '⬡ MCD' : activeData === 'sql' ? '▶ SQL' : '● LIVE'}
              </div>
              <div className="viz-slot" style={{ display: activeData === 'mcd'    ? 'block' : 'none' }}><MCDCanvas    active={activeData === 'mcd'}    /></div>
              <div className="viz-slot" style={{ display: activeData === 'stream' ? 'block' : 'none' }}><DataStreamCanvas active={activeData === 'stream'} /></div>
              <div className="viz-slot" style={{ display: activeData === 'radar'  ? 'block' : 'none' }}><RadarCanvas  active={activeData === 'radar'}  /></div>
              <div className="viz-slot" style={{ display: activeData === 'sql'    ? 'block' : 'none' }}><SQLLiveCanvas active={activeData === 'sql'}   /></div>
              <div className="viz-legend">
                <span className="legend-dot" />
                {LEG[activeData].main}
                <span className="legend-sep">·</span>
                <span className="legend-sub">{LEG[activeData].sub}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════ SÉPARATEUR ══════════════ */}
        <div className="lab-separator">
          <div className="lab-separator-line" />
          <div className="lab-separator-label">
            <span className="lab-separator-icon">◈</span>
            Machine Learning
            <span className="lab-separator-icon">◈</span>
          </div>
          <div className="lab-separator-line" />
        </div>

        {/* ══════════════ BLOC IA ══════════════ */}
        <div className="lab-block">
          <div className="lab-block-header">
            <div className="lab-block-badge lab-block-badge--ia">
              <span className="lab-block-badge-dot lab-block-badge-dot--ia" />
              IA Lab
            </div>
            <p className="lab-block-desc">Algorithmes de machine learning entraînés in-browser · zéro API · 100% JS</p>
          </div>

          <div className="dataia-viz-block">
            <div className="viz-tabs">
              {ML_TABS.map(t => renderTabBtn(t, activeML, setActiveML, 'ml'))}
            </div>

            <div className={`viz-canvas-wrap glass-card ${activeML === 'ml' ? 'ml-active' : ''} ${activeML === 'kmeans' ? 'kmeans-active' : ''} ${activeML === 'reco' ? 'reco-active' : ''}`}>
              <div className="viz-live-badge" style={{ borderColor: 'rgba(160,100,255,0.3)', color: 'rgba(160,100,255,0.9)', background: 'rgba(160,100,255,0.08)' }}>
                {activeML === 'ml' ? '◎ ML' : activeML === 'kmeans' ? '⬡ K-Means' : '✦ Reco'}
              </div>
              <div className="viz-slot" style={{ display: activeML === 'ml'     ? 'block' : 'none' }}><MLPredCanvas  active={activeML === 'ml'}     /></div>
              <div className="viz-slot" style={{ display: activeML === 'kmeans' ? 'block' : 'none' }}><KMeansCanvas  active={activeML === 'kmeans'} /></div>
              <div className="viz-slot" style={{ display: activeML === 'reco'   ? 'block' : 'none' }}><RecoCanvas    active={activeML === 'reco'}   /></div>
              <div className="viz-legend">
                <span className="legend-dot" style={{ background: 'rgba(160,100,255,1)', boxShadow: '0 0 6px rgba(160,100,255,0.8)' }} />
                {LEG[activeML].main}
                <span className="legend-sep">·</span>
                <span className="legend-sub">{LEG[activeML].sub}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════ INFO PANEL ══════════════ */}
        {infoTab && <InfoPanel tabId={infoTab} onClose={() => setInfoTab(null)} />}

        {/* ══════════════ AI CASES ══════════════ */}
        <div className="lab-separator lab-separator--cases">
          <div className="lab-separator-line" />
          <div className="lab-separator-label">
            <span className="lab-separator-icon">✦</span>
            Applications concrètes
            <span className="lab-separator-icon">✦</span>
          </div>
          <div className="lab-separator-line" />
        </div>

        <div className="ai-cases-header">
          <h3 className="ai-cases-title">Comment l'IA peut <span className="gradient-text">booster</span> votre site ?</h3>
          <p className="ai-cases-sub">Applications concrètes que je peux intégrer dans vos projets</p>
        </div>
        <div className="ai-cases-grid">
          {AI_CASES.map((item, i) => (
            <AICaseCard key={item.title} item={item} index={i} visible={visible} />
          ))}
        </div>

        <div className="dataia-cta">
          <p className="dataia-cta-text">Vous avez un projet qui mêle web et données ?</p>
          <a href="#contact" className="btn-primary-dark">
            <span>Discutons-en</span>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        </div>

      </div>
    </section>
  )
}
